// src/pages/CalendarPage.js
import React, { useEffect, useState } from "react";
import { Calendar as BigCalendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import AddEventModal from "../components/calendar/AddEventModal";
import EventDetailsModal from "../components/calendar/EventDetailsModal";
import EditEventModal from "../components/calendar/EditEventModal";
import EventInvitationsModal from "../components/EventInvitationsModal";
import MiniCalendar from "../components/calendar/MiniCalendar";
import SubscriptionModal from "../components/calendar/SubscriptionModal";
import axios from "axios";
import Navbar from "../Navbar";
import { Form, InputGroup, Dropdown, DropdownButton, Container, Row, Col, Card, Button, Badge, Spinner, Modal, Tabs, Tab } from 'react-bootstrap';
import { saveAs } from 'file-saver';
import { useLocation, Link } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import PageTitle from '../components/PageTitle';
import { Calendar1, Info, LifeBuoy, CalendarDays, Rows3, Mailbox, CalendarSearch, Forward, PanelRightOpen, PanelRightClose, BadgePlus, MapPinHouse, CalendarCheck, Clock, CalendarFold, CalendarPlus, CirclePlus, CircleX, Share2, Copy, TriangleAlert, X, ChevronLeft, ChevronRight, CalendarX, Download, Printer, BadgeMinus} from 'lucide-react';
import {
  buildPrintDocument,
  openPrintWindow
} from '../utils/printUtility';

const localizer = momentLocalizer(moment);

// Add this color mapping at the top of the file, after the imports
const eventTypeColors = {
  'Clinic': '#aec6e4', // Blue
  'OR': '#ffb3ba',     // Red
  'Meeting': '#b2e0b2', // Green
  'Education': '#ffdfba', // Yellow
  'Journal Club': '#dbffba', // lime 
  'Personal': '#c3b1e1', // Purple
  'Vacation': '#c7eff7', // Blue
  'Sick': '#e9f4b1', // Yellow
  'Weekend Call': '#fab6a3', // Red
  'Birthday': '#FF69B4', // Pink
  'Other': '#b2e7e0', // Gray
  'default': '#aec6e4'  // Default blue
};


// Vibrant color palette for subscribed calendars
const subscriptionColors = [
  '#FF6B6B', // Vibrant Red
  '#4ECDC4', // Vibrant Teal
  '#45B7D1', // Vibrant Blue
  '#96CEB4', // Vibrant Green
  '#FFEAA7', // Vibrant Yellow
  '#DDA0DD', // Vibrant Plum
  '#FF8C42', // Vibrant Orange
  '#A8E6CF', // Vibrant Mint
  '#FF69B4', // Vibrant Pink
  '#9370DB', // Vibrant Purple
  '#20B2AA', // Vibrant Sea Green
  '#FF6347', // Vibrant Tomato
  '#32CD32', // Vibrant Lime Green
  '#FFD700', // Vibrant Gold
  '#FF1493', // Vibrant Deep Pink
  '#00CED1', // Vibrant Dark Turquoise
];



// Add this style to hide the default time
const customStyles = `
  .hide-default-time .rbc-event-label {
    display: none;
  }

 .rbc-slot-selection {
  background-color: rgba(59, 130, 246, 0.1) !important;
  border-radius: 6px;
  border: 1px dashed var(--blue) !important;
  padding: 5px;
  margin-top: 1px;
  color: var(--blue) !important;
 }
  .rbc-selected-cell {
  background-color: rgba(59, 130, 246, 0.1) !important;
  margin: 3px 0;
 }


  .rbc-time-slot {
    transition: background-color 0.2s ease;
  }

  .rbc-time-slot:hover {
    background-color: rgba(59, 130, 246, 0.05);
  }

  .rbc-time-content {
    border-radius: 0 0 8px 8px;
  }

  .rbc-time-header-content {
    border-radius: 8px 8px 0 0;
  }

  .rbc-time-header.rbc-overflowing {
    max-height: 120px !important;
  }

  .rbc-time-view .rbc-allday-cell {
    overflow: auto !important;

  }

  /*
  .event-hover-effect {
    transition: all 0.2s ease-in-out;
    cursor: pointer;
  }

  .event-hover-effect:hover {
    transform: translateY(-2px);
  }
  */

  /* Fix sidebar width issues */
  .fixed-column {
    width: 280px !important;
    max-width: 280px !important;
    min-width: 280px !important;
    padding: 0.20rem 0.5rem 0rem 0.5rem;
    flex: 0 0 280px !important;
    flex-shrink: 0 !important;
  }


  .mini-calendar-sidebar.collapsed {
    width: 0;
    min-width: 0;
    overflow: hidden;
    padding: 0;
    margin: 0;
  }

  .mini-calendar-sidebar .react-calendar,
  .mobile-mini-calendar .react-calendar {
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 6px;
    overflow: hidden;
    background-color: transparent !important;
    padding: 0 !important;
    font-family: inherit;
  }

  /* Navigation Header */
  .mini-calendar-sidebar .react-calendar__navigation,
  .mobile-mini-calendar .react-calendar__navigation {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding: 0;
  }

  /* Navigation Arrows - Light grey rounded squares */
  .mini-calendar-sidebar .react-calendar__navigation button:first-child,
  .mini-calendar-sidebar .react-calendar__navigation button:last-child,
  .mobile-mini-calendar .react-calendar__navigation button:first-child,
  .mobile-mini-calendar .react-calendar__navigation button:last-child {
    background-color: var(--gray-100) !important;
    border: none !important;
    border-radius: 6px !important;
    color: var(--gray-700) !important;
    padding: 0.5rem !important;
    width: 36px !important;
    height: 36px !important;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s;
  }

  .mini-calendar-sidebar .react-calendar__navigation button:first-child i,
  .mini-calendar-sidebar .react-calendar__navigation button:last-child i,
  .mobile-mini-calendar .react-calendar__navigation button:first-child i,
  .mobile-mini-calendar .react-calendar__navigation button:last-child i {
    padding: 0.37rem 0.7rem !important;
    border-radius: 3px !important;
    background-color: var(--gray-100) !important;
  }

  .mini-calendar-sidebar .react-calendar__navigation button:first-child:hover,
  .mini-calendar-sidebar .react-calendar__navigation button:last-child:hover,
  .mobile-mini-calendar .react-calendar__navigation button:first-child:hover,
  .mobile-mini-calendar .react-calendar__navigation button:last-child:hover {
    background-color: var(--gray-100) !important;
  }

  .mini-calendar-sidebar .react-calendar__navigation button:first-child:hover i,
  .mini-calendar-sidebar .react-calendar__navigation button:last-child:hover i,
  .mobile-mini-calendar .react-calendar__navigation button:first-child:hover i,
  .mobile-mini-calendar .react-calendar__navigation button:last-child:hover i {
    padding: 0.37rem 0.7rem !important;
    border-radius: 3px !important;
    background-color: var(--white-alt) !important;
  }

  .mini-calendar-sidebar .react-calendar__navigation button:disabled:,
  .mobile-mini-calendar .react-calendar__navigation button:disabled {
    opacity: 1 !important;
    cursor: not-allowed;
  }

    .react-calendar__navigation button:disabled {
      background-color: var(--card-bg) !important;
      color: var(--gray-800) !important;
      font-weight: 600 !important;
    }

  /* Month/Year Label - Centered, bold, dark grey */
  .mini-calendar-sidebar .react-calendar__navigation__label,
  .mobile-mini-calendar .react-calendar__navigation__label {
    flex-grow: 1;
    text-align: center;
    pointer-events: none;
  }

  .mini-calendar-sidebar .react-calendar__navigation__label button,
  .mobile-mini-calendar .react-calendar__navigation__label button {
    pointer-events: auto;
    background: none !important;
    border: none !important;
    padding: 0 !important;
    font-size: 1rem !important;
    font-weight: 600 !important;
    color: var(--gray-800) !important;
  }

  /* Weekday Headers */
  .mini-calendar-sidebar .react-calendar__month-view__weekdays,
  .mobile-mini-calendar .react-calendar__month-view__weekdays {
    text-align: center;
    text-transform: none !important;
  }

  .mini-calendar-sidebar .react-calendar__month-view__weekdays__weekday,
  .mobile-mini-calendar .react-calendar__month-view__weekdays__weekday {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--gray-700) !important;
    text-align: center;
  }

  .mini-calendar-sidebar .react-calendar__month-view__weekdays__weekday abbr,
  .mobile-mini-calendar .react-calendar__month-view__weekdays__weekday abbr {
    text-decoration: none;
    border: none;
    cursor: default;
  }

  /* Date Grid */
  .mini-calendar-sidebar .react-calendar__month-view__days,
  .mobile-mini-calendar .react-calendar__month-view__days {
    gap: 0;
  }

  .mini-calendar-sidebar .react-calendar__month-view__days__day,
  .mobile-mini-calendar .react-calendar__month-view__days__day {
    padding: 0.75rem 0 !important;
    font-size: 0.95rem;
    color: var(--gray-800) !important;
    text-align: center;
    border-radius: 8px !important;
    transition: all 0.2s;
  }

  /* Previous/Next Month Dates - Grayed out */
  .mini-calendar-sidebar .react-calendar__month-view__days__day--neighboringMonth,
  .mobile-mini-calendar .react-calendar__month-view__days__day--neighboringMonth {
    color: var(--gray-500) !important;
  }

  /* Today - Keep subtle, not highlighted */
  .mini-calendar-sidebar .react-calendar__tile--now,
  .mobile-mini-calendar .react-calendar__tile--now {
    background-color: transparent !important;
    color: var(--gray-800) !important;
    font-weight: 500;
  }

  /* Selected/Active Date - Dark black background, white text, rounded */
  .mini-calendar-sidebar .react-calendar__tile--active,
  .mobile-mini-calendar .react-calendar__tile--active {
    background-color: var(--gray-800) !important;
    color: var(--white) !important;
    font-weight: 700 !important;
    border-radius: 8px !important;
  }

  .mini-calendar-sidebar .react-calendar__tile--active:enabled:hover,
  .mini-calendar-sidebar .react-calendar__tile--active:enabled:focus,
  .mobile-mini-calendar .react-calendar__tile--active:enabled:hover,
  .mobile-mini-calendar .react-calendar__tile--active:enabled:focus {
    background-color: var(--gray-800) !important;
    color: var(--white) !important;
  }

  /* Hover effect for dates */
  .mini-calendar-sidebar .react-calendar__tile:enabled:hover,
  .mini-calendar-sidebar .react-calendar__tile:enabled:focus,
  .mobile-mini-calendar .react-calendar__tile:enabled:hover,
  .mobile-mini-calendar .react-calendar__tile:enabled:focus {
    background-color: var(--gray-300) !important;
    border-radius: 8px !important;
    color: var(--gray-700) !important;
  }

  /* Don't override active state on hover */
  .mini-calendar-sidebar .react-calendar__tile--active:enabled:hover,
  .mini-calendar-sidebar .react-calendar__tile--active:enabled:focus,
  .mobile-mini-calendar .react-calendar__tile--active:enabled:hover,
  .mobile-mini-calendar .react-calendar__tile--active:enabled:focus {
    background-color: var(--gray-800) !important;
    color: var(--white) !important;
  }

  /* Remove default tile styling */
  .mini-calendar-sidebar .react-calendar__tile,
  .mobile-mini-calendar .react-calendar__tile {
    background: none;
    border: none;
  }

  /* Weekend dates - same as weekdays */
  .mini-calendar-sidebar .react-calendar__month-view__days__day--weekend:not(.react-calendar__tile--active),
  .mobile-mini-calendar .react-calendar__month-view__days__day--weekend:not(.react-calendar__tile--active) {
    color: var(--gray-500) !important;
  }

  .mini-calendar-sidebar .react-calendar__month-view__days__day--weekend.react-calendar__month-view__days__day--neighboringMonth,
  .mobile-mini-calendar .react-calendar__month-view__days__day--weekend.react-calendar__month-view__days__day--neighboringMonth {
    color: var(--gray-500) !important;
  }

  /* Event dots */
  .mini-calendar-sidebar .react-calendar__tile--hasEvent,
  .mobile-mini-calendar .react-calendar__tile--hasEvent {
    position: relative;
  }

  .mini-calendar-sidebar .react-calendar__tile--hasEvent div span,
  .mobile-mini-calendar .react-calendar__tile--hasEvent div span {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    display: inline-block;
    margin-top: 2px;
  }

  /* Remove abbr tooltips */
  .mini-calendar-sidebar .react-calendar__month-view__weekdays__weekday abbr[title],
  .mobile-mini-calendar .react-calendar__month-view__weekdays__weekday abbr[title] {
    pointer-events: none;
  }

  /* Year/Decade view adjustments */
  .mini-calendar-sidebar .react-calendar__year-view .react-calendar__tile,
  .mobile-mini-calendar .react-calendar__year-view .react-calendar__tile {
    padding: 1rem 0.5rem !important;
  }

  .mini-calendar-event-dots {
    display: flex;
    justify-content: center;
    gap: 1px;
    margin-top: 1px;
  }

  .mini-calendar-event-dot {
    width: 3px;
    height: 3px;
    border-radius: 50%;
    display: inline-block;
  }

  .mini-calendar-event-dot-more {
    background: #6c757d !important;
    font-size: 0.5rem;
    width: auto;
    height: auto;
    padding: 0 1px;
    border-radius: 2px;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .mini-calendar-tile--hasEvent {
    position: relative;
  }

  /* Week selection styling - use gray-800 to match dashboard theme */
  .mini-calendar-tile--selected-week {
    background: var(--gray-800) !important;
    color: var(--white) !important;
    font-weight: 700;
    border-radius: 8px !important;
  }

  .mini-calendar-tile--selected-week:enabled:hover,
  .mini-calendar-tile--selected-week:enabled:focus {
    background: var(--gray-800) !important;
    color: var(--white) !important;
  }

  /* Ensure week selection takes precedence over other styles */
  .mini-calendar-sidebar .react-calendar__tile.mini-calendar-tile--selected-week,
  .mobile-mini-calendar .react-calendar__tile.mini-calendar-tile--selected-week {
    background: var(--gray-800) !important;
    color: var(--white) !important;
    font-weight: 700;
    border-radius: 8px !important;
  }

  /* Week selection styling - override border radius for all selected week tiles */
  .mini-calendar-sidebar .react-calendar__tile.mini-calendar-tile--selected-week,
  .mobile-mini-calendar .react-calendar__tile.mini-calendar-tile--selected-week {
    border-radius: 0 !important;
    background: var(--gray-300) !important;
    color: var(--gray-700) !important;
  }

  /* Keep current day styling when part of selected week */
  .mini-calendar-sidebar .react-calendar__tile.mini-calendar-tile--selected-week.mini-calendar-tile--today,
  .mobile-mini-calendar .react-calendar__tile.mini-calendar-tile--selected-week.mini-calendar-tile--today {
    background-color: var(--gray-900) !important;
    color: var(--white-alt) !important;
    font-weight: 500 !important;
  }

  .mini-calendar-sidebar .react-calendar__tile.mini-calendar-tile--selected-week.mini-calendar-tile--today:enabled:hover,
  .mini-calendar-sidebar .react-calendar__tile.mini-calendar-tile--selected-week.mini-calendar-tile--today:enabled:focus,
  .mobile-mini-calendar .react-calendar__tile.mini-calendar-tile--selected-week.mini-calendar-tile--today:enabled:hover,
  .mobile-mini-calendar .react-calendar__tile.mini-calendar-tile--selected-week.mini-calendar-tile--today:enabled:focus {
    background-color: var(--gray-900) !important;
    color: var(--white-alt) !important;
  }

  /* Week selection styling - first day of week (Sunday) */
  .mini-calendar-sidebar .react-calendar__month-view__days__day:nth-child(7n+1).mini-calendar-tile--selected-week,
  .mobile-mini-calendar .react-calendar__month-view__days__day:nth-child(7n+1).mini-calendar-tile--selected-week {
    border-top-left-radius: 8px !important;
    border-bottom-left-radius: 8px !important;
  }

  /* Week selection styling - last day of week (Saturday) */
  .mini-calendar-sidebar .react-calendar__month-view__days__day:nth-child(7n).mini-calendar-tile--selected-week,
  .mobile-mini-calendar .react-calendar__month-view__days__day:nth-child(7n).mini-calendar-tile--selected-week {
    border-top-right-radius: 8px !important;
    border-bottom-right-radius: 8px !important;
  }

  /* Week selection hover - no border-radius for middle days */
  .mini-calendar-sidebar .react-calendar__tile.mini-calendar-tile--selected-week:enabled:hover,
  .mini-calendar-sidebar .react-calendar__tile.mini-calendar-tile--selected-week:enabled:focus,
  .mobile-mini-calendar .react-calendar__tile.mini-calendar-tile--selected-week:enabled:hover,
  .mobile-mini-calendar .react-calendar__tile.mini-calendar-tile--selected-week:enabled:focus {
    background: var(--gray-900) !important;
    color: var(--white-alt) !important;
    border-radius: 0 !important;
  }

  /* Week selection hover - first day (left border-radius only) */
  .mini-calendar-sidebar .react-calendar__month-view__days__day:nth-child(7n+1).mini-calendar-tile--selected-week:enabled:hover,
  .mini-calendar-sidebar .react-calendar__month-view__days__day:nth-child(7n+1).mini-calendar-tile--selected-week:enabled:focus,
  .mobile-mini-calendar .react-calendar__month-view__days__day:nth-child(7n+1).mini-calendar-tile--selected-week:enabled:hover,
  .mobile-mini-calendar .react-calendar__month-view__days__day:nth-child(7n+1).mini-calendar-tile--selected-week:enabled:focus {
    border-top-left-radius: 8px !important;
    border-bottom-left-radius: 8px !important;
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }

  /* Week selection hover - last day (right border-radius only) */
  .mini-calendar-sidebar .react-calendar__month-view__days__day:nth-child(7n).mini-calendar-tile--selected-week:enabled:hover,
  .mini-calendar-sidebar .react-calendar__month-view__days__day:nth-child(7n).mini-calendar-tile--selected-week:enabled:focus,
  .mobile-mini-calendar .react-calendar__month-view__days__day:nth-child(7n).mini-calendar-tile--selected-week:enabled:hover,
  .mobile-mini-calendar .react-calendar__month-view__days__day:nth-child(7n).mini-calendar-tile--selected-week:enabled:focus {
    border-top-right-radius: 8px !important;
    border-bottom-right-radius: 8px !important;
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
  }

  /* Selected day in week view - should override week selection */
  .mini-calendar-sidebar .react-calendar__tile.mini-calendar-tile--selected-week.mini-calendar-tile--selected-day,
  .mobile-mini-calendar .react-calendar__tile.mini-calendar-tile--selected-week.mini-calendar-tile--selected-day {
    background: var(--gray-800) !important;
    color: var(--white) !important;
  }



  .mobile-mini-calendar {
    background: white;
    border-radius: 8px;
    padding: 12px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    margin-bottom: 16px;
  }

  /* Event Type Filter Styles */
  .event-type-filter {
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    padding-top: 1rem;
  }

  .event-types-list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .event-type-item {
    padding: 0.5rem;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .event-type-item:hover {
    background-color: var(--gray-100) !important;
  }

  .event-type-item.active {
    font-weight: 600;
  }

  .event-type-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .show-more-container {
    text-align: center;
  }

  .show-more-container button {
    text-decoration: none;
    border: none;
    background: none;
    color: var(--text-muted);
    transition: color 0.2s ease;
  }

  .show-more-container button:hover {
    color: var(--blue);
    text-decoration: none;
  }

  /* Calendar Subscriptions Styles */
  .calendar-subscriptions {
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    padding-top: 1rem;
  }

  .subscriptions-list {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
  }

  .subscription-item {
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid transparent;
  }

  .subscription-item:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }

  .subscription-status {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .subscription-status.active {
    background-color: var(--green);
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
  }

  .subscription-status.inactive {
    background-color: #6B7280;
    box-shadow: 0 0 0 2px rgba(107, 114, 128, 0.2);
  }

  /* Share Calendar Styles */
  .share-calendar {
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    padding-top: 1rem;
  }

  .share-options {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .share-option {
    padding: 0.5rem;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid transparent;
  }

  .share-option:hover {
    background-color: rgba(0, 0, 0, 0.05);
    transform: translateX(2px);
  }

  .share-status {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .share-status.active {
    background-color: var(--green);
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
  }

  .share-status.inactive {
    background-color: #6B7280;
    box-shadow: 0 0 0 2px rgba(107, 114, 128, 0.2);
  }

  /* Subscription Event Styles */
  .subscription-event {
    position: relative;
  }

  .subscription-event::before {
    content: '';
    position: absolute;
    top: 2px;
    right: 2px;
    width: 6px;
    height: 6px;
    background-color: #666;
    border-radius: 50%;
    opacity: 0.7;
  }

  /* Hide dropdown arrow for color picker */
  .subscription-item .dropdown-toggle::after {
    display: none !important;
  }


  /* Calendar Menu Styles */
  .calendar-color-indicator {
    flex-shrink: 0;
  }

  .my-calendar-toggle .form-check-input:checked {
    background-color: var(--blue);
    border-color: var(--blue-dark);
  }

  .my-calendar-toggle .form-check-input:focus {
    border-color: var(--blue);
    box-shadow: 0 0 0 0.25rem rgba(0, 123, 255, 0.25);
  }

  /* Highlighted day styles for React Big Calendar */
  .rbc-day-bg.rbc-selected {
    background-color: var(--blue-light) !important;
    border-radius: 4px !important;
    transition: all 0.2s ease-in-out;
  }

  /* Ensure the highlighting works in all views */
  .rbc-month-view .rbc-day-bg.rbc-selected,
  .rbc-day-view .rbc-day-bg.rbc-selected {
    background-color: var(--blue-light) !important;
    border-radius: 4px !important;
    transition: all 0.2s ease-in-out;
  }

  /* Week view: only highlight the date header, not the background */
  .rbc-week-view .rbc-day-bg.rbc-selected {
    background-color: transparent !important;
    border: none !important;
  }

  /* Week view: highlight the date header */
  .rbc-week-view .rbc-header.rbc-selected,
  .rbc-time-view .rbc-header.rbc-selected {
    background-color: var(--blue-light) !important;
    border-radius: 4px !important;
    transition: all 0.2s ease-in-out;
    color: var(--blue) !important;
  }

  /* Make sure the highlighting is visible over other styles */
  .rbc-day-bg.rbc-selected.rbc-today {
    background-color: var(--blue-light) !important;
  }

  /* Week view: today highlighting for date header */
  .rbc-week-view .rbc-header.rbc-selected.rbc-today,
  .rbc-time-view .rbc-header.rbc-selected.rbc-today {
    background-color: var(--blue-light) !important;
    color: var(--blue) !important;
  }



`;

// Add this component right after the imports
const CustomStyles = () => (
  <style>{customStyles}</style>
);

// Add this component after the imports
const CustomHeader = ({ date, view, selectedDay }) => {
  const isSelected = selectedDay && moment(date).isSame(moment(selectedDay), 'day');
  const isToday = moment(date).isSame(moment(), 'day');
  
  let className = 'rbc-header';
  if (isToday) className += ' rbc-today';
  if (isSelected) className += ' rbc-selected';
  
  return (
    <div className={className}>
      {moment(date).format('ddd D')}
    </div>
  );
};

const CustomEvent = ({ event, view }) => {
  // For month view, show a simpler layout
  if (view === 'month') {
    return (
      <div className="d-flex flex-column gap-1 px-2 py-1" style={{ fontSize: '0.8rem' }}>
        <div className="d-flex align-items-center gap-1">
          <strong><span>{event.label}</span></strong>
          <span className="text-truncate">
            {event.title}
          </span>
        </div>
      </div>
    );
  }

  // For week and day views, show the detailed layout
  return (
    <div className={`d-flex ${event.allDay ? 'flex-row gap-1' : 'flex-column'} gap-1 px-2 py-1`} style={{ fontSize: '0.8rem' }}>
              <div className="d-flex align-items-center gap-1">
      <span>
        <strong>
          {event.label || 'Event'}
        </strong>
      </span>
        </div>
      <span>
        {event.title}
      </span>
      {!event.allDay && (
        <span className="text-secondary mt-1" style={{ fontSize: '0.75rem' }}>
          {moment.utc(event.start).local().format('h:mm A')} - {moment.utc(event.end).local().format('h:mm A')}
        </span>
      )}
    </div>
  );
};

const CustomToolbar = (toolbar) => {
  const goToToday = () => {
    toolbar.onNavigate('TODAY');
  };

  // Determine if mobile
  const isMobile = window.innerWidth <= 768;
  // Shorten the label for mobile
  let shortLabel = toolbar.label;
  if (isMobile) {
    // Try to parse the label using moment
    // For month view, label is usually 'Month YYYY'
    // For week/day, label is usually 'Month D – D, YYYY' or 'Month D, YYYY'
    if (toolbar.view === 'month') {
      // Try to get the current date from toolbar.date
      shortLabel = moment(toolbar.date).format('MMM YYYY');
    } else if (toolbar.view === 'week' || toolbar.view === 'day') {
      shortLabel = moment(toolbar.date).format('MMM D, YYYY');
    }
  }

  return (
    <div className="rbc-toolbar">
      <div className="rbc-btn-group">
        <button type="button" className="px-2 py-1" onClick={() => toolbar.onNavigate('PREV')}>
          <ChevronLeft/>
        </button>
        <button type="button" className="px-2 py-1" onClick={goToToday}>
          {window.innerWidth <= 768 ? (
            <Calendar1/>
          ) : (
            'Today'
          )}
        </button>
        <button type="button" className="px-2 py-1" onClick={() => toolbar.onNavigate('NEXT')}>
          <ChevronRight/>
        </button>
      </div>
      <span className="rbc-toolbar-label">{isMobile ? shortLabel : toolbar.label}</span>
      {window.innerWidth <= 768 ? (
  <div className="rbc-btn-group">
    <Form.Select
      size="sm"
      value={toolbar.view}
      onChange={e => toolbar.onView(e.target.value)}
      style={{ minWidth: 90 }}
    >
      <option value="month">Month</option>
      <option value="week">Week</option>
      <option value="day">Day</option>
    </Form.Select>
  </div>
) : (
  <div className="rbc-btn-group">
    <button type="button" className={toolbar.view === 'month' ? 'rbc-active' : ''} onClick={() => toolbar.onView('month')}>
      Month
    </button>
    <button type="button" className={toolbar.view === 'week' ? 'rbc-active' : ''} onClick={() => toolbar.onView('week')}>
      Week
    </button>
    <button type="button" className={toolbar.view === 'day' ? 'rbc-active' : ''} onClick={() => toolbar.onView('day')}>
      Day
    </button>
  </div>
)}
    </div>
  );
};

// Add this new component after the CustomToolbar component
const MonthMenu = ({ currentMonth, onMonthChange }) => {
  const months = Array.from({ length: 12 }, (_, i) => {
    const date = new Date();
    date.setMonth(i);
    return {
      value: date.toISOString().slice(0, 7), // YYYY-MM format
      label: date.toLocaleString('default', { month: 'long' })
    };
  });

  const handleTodayClick = () => {
    const today = moment().format('YYYY-MM');
    onMonthChange(today);
    
    // Scroll to today's events after a short delay to ensure the list is rendered
    setTimeout(() => {
      const todayElement = document.querySelector(`[data-date="${moment().format('YYYY-MM-DD')}"]`);
      if (todayElement) {
        todayElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  return (

      <Card className="stuck-tp rounded-0 border-0 border-bottom mb-3">
        <Card.Body >
      <div className="d-flex justify-content-between align-items-center">
        <div className="radio-inputs" style={{ width: 'fit-content' }}>
          <label className="radio">
            <input
              type="radio"
              name="month-radio"
              checked={false}
              onChange={() => {
                const prevMonth = moment(currentMonth).subtract(1, 'month').format('YYYY-MM');
                onMonthChange(prevMonth);
              }}
            />
            <span className="name bg-white-hover px-2"><ChevronLeft/></span>
          </label>
          <div className="dropdown-container">
            <Dropdown>
            <Dropdown.Toggle
              variant="light-solid"
              style={{
                minWidth: 'auto',
                textAlign: 'left'
              }}
              size="sm"
            >
              {moment(currentMonth).format('MMMM YYYY')}
            </Dropdown.Toggle>
            <Dropdown.Menu style={{
              maxHeight: '250px',
              overflowY: 'auto',
              width: 'auto',
              minWidth: 'auto'
            }}>
              {months.map((month) => (
                <Dropdown.Item
                  key={month.value}
                  onClick={() => onMonthChange(month.value)}
                  active={month.value === currentMonth}
                  style={{
                    padding: '8px 16px',
                    cursor: 'pointer'
                  }}
                >
                  {month.label} {moment(month.value).format('YYYY')}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          </div>
          <label className="radio">
            <input
              type="radio"
              name="month-radio"
              checked={false}
              onChange={() => {
                const nextMonth = moment(currentMonth).add(1, 'month').format('YYYY-MM');
                onMonthChange(nextMonth);
              }}
            />
            <span className="name bg-white-hover px-2"><ChevronRight/></span>
          </label>
          
          
        </div>

        <Button
          variant="light-solid"
          onClick={handleTodayClick}
          size="sm"
        >
          <div className="d-flex align-items-center"><Calendar1/> <span className="d-none d-md-inline ms-2">Today</span></div>
        </Button>
      </div>
      </Card.Body>
      </Card>
       
  );
};

// Helper function to fetch events from iCal URL
const fetchEventsFromUrl = async (url, subscriptionName) => {
  try {
    console.log(`Fetching events from URL: ${url} for subscription: ${subscriptionName}`);
    
    // Try to fetch the iCal file from the URL
    // Note: This might fail due to CORS restrictions for external URLs
    let response;
    try {
      console.log(`Attempting direct fetch for: ${url}`);
      response = await fetch(url);
    } catch (corsError) {
      console.warn(`CORS error fetching ${url}, trying proxy approach...`);
      // For external URLs, we might need to use a proxy or backend endpoint
      // For now, we'll use a CORS proxy as a fallback
      const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`;
      console.log(`Trying proxy URL: ${proxyUrl}`);
      response = await fetch(proxyUrl);
    }
    
    if (!response.ok) {
      throw new Error(`Failed to fetch iCal from ${url}: ${response.status}`);
    }
    
    const icalData = await response.text();
    console.log(`Successfully fetched iCal data for ${subscriptionName}:`, icalData.substring(0, 200) + '...');
    return parseICalData(icalData, subscriptionName);
      } catch (error) {
      console.error(`Error fetching events from URL ${url}:`, error);
      
      // If we can't fetch from the URL, try to provide some fallback events
      // This could be based on the subscription name or other criteria
      if (subscriptionName.toLowerCase().includes('holiday')) {
        return [
          {
            id: `fallback_${subscriptionName}_1`,
            title: 'New Year\'s Day',
            start: moment().startOf('year').toDate(),
            end: moment().startOf('year').add(1, 'day').subtract(1, 'second').toDate(),
            allDay: true,
            label: 'Holiday',
            description: 'New Year\'s Day holiday',
            location: '',
            source: 'subscription',
            subscriptionName: subscriptionName
          },
          {
            id: `fallback_${subscriptionName}_2`,
            title: 'Christmas Day',
            start: moment().month(11).date(25).toDate(),
            end: moment().month(11).date(25).add(1, 'day').subtract(1, 'second').toDate(),
            allDay: true,
            label: 'Holiday',
            description: 'Christmas Day holiday',
            location: '',
            source: 'subscription',
            subscriptionName: subscriptionName
          }
        ];
      }
      
      return [];
    }
};

// Helper function to parse iCal data
const parseICalData = (icalData, subscriptionName) => {
  const events = [];
  const lines = icalData.split('\n');
  let currentEvent = null;
  let inEvent = false;
  let currentLine = '';
  
  console.log(`Starting to parse iCal data for ${subscriptionName}. Total lines: ${lines.length}`);
  
  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];
    
    // Handle line folding (iCal can have long lines split with space/tab)
    if (line.startsWith(' ') || line.startsWith('\t')) {
      currentLine += line.substring(1);
    } else {
      if (currentLine) {
        line = currentLine + line;
        currentLine = '';
      }
    }
    
    line = line.trim();
    if (!line) continue;
    
    if (line === 'BEGIN:VEVENT') {
      inEvent = true;
      currentEvent = {
        id: `sub_${subscriptionName}_${Date.now()}_${Math.random()}`,
        source: 'subscription',
        subscriptionName: subscriptionName,
        allDay: false,
        label: 'Other' // Default label for subscribed events
      };
      console.log('Found BEGIN:VEVENT, starting new event');
    } else if (line === 'END:VEVENT') {
      if (currentEvent && currentEvent.title && currentEvent.start) {
        // Ensure we have an end time
        if (!currentEvent.end) {
          if (currentEvent.allDay) {
            currentEvent.end = moment(currentEvent.start).add(1, 'day').subtract(1, 'second').toDate();
          } else {
            currentEvent.end = moment(currentEvent.start).add(1, 'hour').toDate();
          }
        }
        
        // Try to determine event type from title/description
        const title = currentEvent.title.toLowerCase();
        const description = (currentEvent.description || '').toLowerCase();
        
        // For US Holidays calendar, all events should be holidays
        if (subscriptionName.toLowerCase().includes('holiday') || subscriptionName.toLowerCase().includes('us holiday')) {
          currentEvent.label = 'Holiday';
        } else if (title.includes('holiday') || description.includes('holiday')) {
          currentEvent.label = 'Holiday';
        } else if (title.includes('meeting') || description.includes('meeting')) {
          currentEvent.label = 'Meeting';
        } else if (title.includes('conference') || description.includes('conference')) {
          currentEvent.label = 'Education';
        } else if (title.includes('vacation') || description.includes('vacation')) {
          currentEvent.label = 'Vacation';
        } else if (title.includes('sick') || description.includes('sick')) {
          currentEvent.label = 'Sick';
        } else if (title.includes('clinic') || description.includes('clinic')) {
          currentEvent.label = 'Clinic';
        } else if (title.includes('or') || description.includes('or') || title.includes('surgery')) {
          currentEvent.label = 'OR';
        }
        
        console.log(`Adding event: ${currentEvent.title} (${currentEvent.start})`);
        events.push(currentEvent);
      } else {
        console.log('Skipping event - missing title or start date:', currentEvent);
      }
      inEvent = false;
      currentEvent = null;
    } else if (inEvent && currentEvent) {
      const colonIndex = line.indexOf(':');
      if (colonIndex > 0) {
        let fullKey = line.substring(0, colonIndex);
        let value = line.substring(colonIndex + 1);
        
        // Extract the base key (before any semicolon)
        const key = fullKey.split(';')[0];
        
        // Handle quoted values
        if (value.startsWith('"') && value.endsWith('"')) {
          value = value.substring(1, value.length - 1);
        }
        
        // Debug: Log all lines being processed
        if (key === 'DTSTART' || key === 'DTEND' || key === 'SUMMARY') {
          console.log(`Processing line: ${line}`);
          console.log(`Full key: ${fullKey}, Base key: ${key}, Value: ${value}`);
        }
        
        switch (key) {
          case 'SUMMARY':
            currentEvent.title = value;
            console.log(`Set title: ${value}`);
            break;
          case 'DESCRIPTION':
            currentEvent.description = value.replace(/\\n/g, '\n').replace(/\\,/g, ',');
            break;
          case 'LOCATION':
            currentEvent.location = value;
            break;
          case 'DTSTART':
            console.log(`Processing DTSTART: ${value}`);
            if (fullKey.includes('VALUE=DATE')) {
              // All-day event
              const date = moment(value, 'YYYYMMDD').toDate();
              currentEvent.start = date;
              currentEvent.allDay = true;
              console.log(`Set all-day start date: ${value} -> ${date}`);
            } else {
              // Regular event with time
              const dateTime = parseICalDateTime(value);
              currentEvent.start = dateTime;
              console.log(`Set start date/time: ${value} -> ${dateTime}`);
            }
            break;
          case 'DTEND':
            console.log(`Processing DTEND: ${value}`);
            if (fullKey.includes('VALUE=DATE')) {
              // All-day event end
              const date = moment(value, 'YYYYMMDD').toDate();
              currentEvent.end = date;
              console.log(`Set all-day end date: ${value} -> ${date}`);
            } else {
              // Regular event end time
              const dateTime = parseICalDateTime(value);
              currentEvent.end = dateTime;
              console.log(`Set end date/time: ${value} -> ${dateTime}`);
            }
            break;
          case 'UID':
            currentEvent.id = `sub_${subscriptionName}_${value}`;
            break;
          case 'CATEGORIES':
            // Try to map categories to our event types
            const categories = value.toLowerCase();
            if (categories.includes('holiday')) {
              currentEvent.label = 'Holiday';
            } else if (categories.includes('meeting')) {
              currentEvent.label = 'Meeting';
            } else if (categories.includes('education') || categories.includes('conference')) {
              currentEvent.label = 'Education';
            }
            break;
          default:
            console.log(`Unhandled iCal field: ${key} = ${value}`);
            break;
        }
      }
    }
  }
  
  console.log(`Parsed ${events.length} events from ${subscriptionName}`);
  return events;
};

// Helper function to parse iCal date-time format
const parseICalDateTime = (dateTimeStr) => {
  try {
    // Handle different iCal date-time formats
    
    // Remove timezone info if present
    let cleanDateTime = dateTimeStr;
    
    // Handle TZID parameter
    if (cleanDateTime.includes('TZID=')) {
      const tzidMatch = cleanDateTime.match(/TZID=([^:]+):(.+)/);
      if (tzidMatch) {
        cleanDateTime = tzidMatch[2];
      }
    }
    
    // Remove Z suffix (UTC)
    cleanDateTime = cleanDateTime.replace(/Z$/, '');
    
    // Parse the date-time string
    if (cleanDateTime.length === 8) {
      // Date only (YYYYMMDD)
      return moment(cleanDateTime, 'YYYYMMDD').toDate();
    } else if (cleanDateTime.length === 15) {
      // Date and time (YYYYMMDDTHHMMSS)
      return moment(cleanDateTime, 'YYYYMMDDTHHmmss').toDate();
    } else if (cleanDateTime.length === 17) {
      // Date and time with seconds (YYYYMMDDTHHMMSS)
      return moment(cleanDateTime, 'YYYYMMDDTHHmmss').toDate();
    } else {
      // Try parsing as ISO string or other formats
      const parsed = new Date(cleanDateTime);
      if (isNaN(parsed.getTime())) {
        // Try moment parsing as fallback
        return moment(cleanDateTime).toDate();
      }
      return parsed;
    }
  } catch (error) {
    console.error('Error parsing iCal date-time:', dateTimeStr, error);
    // Return current date as fallback
    return new Date();
  }
};

// Helper function to fetch events from Google Calendar
const fetchEventsFromGoogle = async (subscriptionName) => {
  try {
    console.log(`Fetching events from Google Calendar for subscription: ${subscriptionName}`);
    
    // In a real implementation, this would use Google Calendar API
    // For now, return mock events
    const mockEvents = [
      {
        id: `google_${subscriptionName}_1`,
        title: `Google ${subscriptionName} Event`,
        start: moment().add(3, 'days').startOf('day').add(14, 'hours').toDate(),
        end: moment().add(3, 'days').startOf('day').add(15, 'hours').toDate(),
        allDay: false,
        label: 'Education',
        description: `Google Calendar event from ${subscriptionName}`,
        location: 'Online'
      }
    ];
    
    return mockEvents;
  } catch (error) {
    console.error(`Error fetching events from Google Calendar for ${subscriptionName}:`, error);
    return [];
  }
};

// Helper function to fetch events from Outlook Calendar
const fetchEventsFromOutlook = async (subscriptionName) => {
  try {
    console.log(`Fetching events from Outlook Calendar for subscription: ${subscriptionName}`);
    
    // In a real implementation, this would use Microsoft Graph API
    // For now, return mock events
    const mockEvents = [
      {
        id: `outlook_${subscriptionName}_1`,
        title: `Outlook ${subscriptionName} Event`,
        start: moment().add(4, 'days').startOf('day').add(11, 'hours').toDate(),
        end: moment().add(4, 'days').startOf('day').add(12, 'hours').toDate(),
        allDay: false,
        label: 'Meeting',
        description: `Outlook Calendar event from ${subscriptionName}`,
        location: 'Meeting Room B'
      }
    ];
    
    return mockEvents;
  } catch (error) {
    console.error(`Error fetching events from Outlook Calendar for ${subscriptionName}:`, error);
    return [];
  }
};



const getGroupedEvents = (events, selectedMonth) => {
  // Filter events for the selected month
  const monthEvents = events.filter(event => {
    const eventStart = moment(event.start);
    const eventEnd = moment(event.end);
    const monthStart = moment(selectedMonth).startOf('month');
    const monthEnd = moment(selectedMonth).endOf('month');
    
    // For all-day events, check if they overlap with the selected month
    if (event.allDay) {
      return eventStart.isSameOrBefore(monthEnd) && eventEnd.isSameOrAfter(monthStart);
    }
    
    // For regular events, check if they start in the selected month
    return eventStart.format('YYYY-MM') === selectedMonth;
  });
  
  // Group events by day
  const groupedEvents = {};
  
  // First, get all days in the selected month
  const monthStart = moment(selectedMonth).startOf('month');
  const monthEnd = moment(selectedMonth).endOf('month');
  let currentDay = monthStart.clone();
  
  while (currentDay.isSameOrBefore(monthEnd)) {
    const dayKey = currentDay.format('YYYY-MM-DD');
    groupedEvents[dayKey] = [];
    currentDay.add(1, 'day');
  }
  
  // Then, distribute events to their respective days
  monthEvents.forEach(event => {
    const eventStart = moment(event.start);
    const eventEnd = moment(event.end);
    
    if (event.allDay) {
      // For all-day events, add them to every day of their duration
      let currentDay = eventStart.clone();
      while (currentDay.isSameOrBefore(eventEnd)) {
        const dayKey = currentDay.format('YYYY-MM-DD');
        if (groupedEvents[dayKey]) {
          groupedEvents[dayKey].push(event);
        }
        currentDay.add(1, 'day');
      }
    } else {
      // For regular events, add them only to their start day
      const dayKey = eventStart.format('YYYY-MM-DD');
      if (groupedEvents[dayKey]) {
        groupedEvents[dayKey].push(event);
      }
    }
  });

  return groupedEvents;
};

export default function CalendarPage({ embedded = false, hidePageChrome = false }) {
  const location = useLocation();
  const [events, setEvents] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [pageTitle] = useState("Calendar");
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState("month");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');
  const [isListView, setIsListView] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(moment().format('YYYY-MM'));
  const [showInvitationsModal, setShowInvitationsModal] = useState(false);
  const [pendingInvitationsCount, setPendingInvitationsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showAllEventTypes, setShowAllEventTypes] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  // Get current user ID for share URLs
  const getCurrentUserId = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      return user?.id || localStorage.getItem('userId');
    } catch (error) {
      console.error('Error getting user ID:', error);
      return null;
    }
  };

  const [shareOptions, setShareOptions] = useState(() => {
    const userId = getCurrentUserId();
    const apiBaseUrl = process.env.NODE_ENV === 'production' 
      ? (process.env.REACT_APP_API_URL || 'https://medhive-i0to.onrender.com')
      : 'http://localhost:5000';
    return [
      { id: 1, name: 'Public Link', status: 'active', url: `${window.location.origin}/calendar?user=${userId}` },
      { id: 2, name: 'iCal Feed', status: 'active', url: `${apiBaseUrl}/api/events/feed/${userId}.ics` },
      { id: 3, name: 'Google Calendar', status: 'inactive', url: null }
    ];
  });
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareModalContent, setShareModalContent] = useState({ title: '', message: '', url: '' });
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [showMyCalendar, setShowMyCalendar] = useState(() => {
    try {
      const saved = localStorage.getItem('showMyCalendar');
      return saved ? JSON.parse(saved) : true;
    } catch (error) {
      console.error('Error loading showMyCalendar from localStorage:', error);
      return true;
    }
  });
  const [showCalendarMenu, setShowCalendarMenu] = useState(false);

  // Single sidebar renderer used by both calendar and list views
  const renderSidebar = () => (
    !sidebarCollapsed && (
      <Col className="d-none d-md-block fixed-column" style={{ 
        flex: '0 0 280px', 
        width: '280px', 
        maxWidth: '280px',
        height: 'calc(100vh - 135px)',
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: 'var(--card-bg)'
      }}>
        <Card className="py-2 border-0" style={{ height: '100%' }}>
          <div className="mini-calendar-sidebar">
            <MiniCalendar
              currentDate={date}
              events={events}
              view={view}
              onDateChange={handleMiniCalendarDateChange}
              onNavigate={handleNavigate}
              eventTypeColors={eventTypeColors}
              selectedDay={selectedDay}
            />
          </div>
          {/* My Calendar Toggle */}
          <div className="my-calendar-toggle mb-3">
            <div className="d-flex justify-content-between align-items-center pt-3 mt-2 border-top border-gray-200">
              <h6 className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
                Calendar
              </h6>
            </div>
            <div className="d-flex align-items-center justify-content-between ps-2">
              <div className="d-flex align-items-center">
                <div 
                  className="calendar-color-indicator me-2"
                  style={{ 
                    backgroundColor: 'var(--blue)',
                    width: '12px',
                    height: '12px',
                    borderRadius: '2px',
                    border: '1px solid rgba(0,0,0,0.1)'
                  }}
                ></div>
                <span style={{ fontSize: '0.75rem' }}>Show My Events</span>
              </div>
              <Form.Check
                type="switch"
                size="sm"
                checked={showMyCalendar}
                onChange={(e) => setShowMyCalendar(e.target.checked)}
                id="sidebar-show-my-calendar"
              />
            </div>
          </div>

          {/* Event Type Filter */}
          <div className="event-type-filter">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="text-muted mb-0 py-1" style={{ fontSize: '0.85rem' }}>
                Filter by Event Type
              </h6>
              {selectedLabel && (
                <Button 
                  variant="light-solid"
                  size="sm"
                  className="px-1 py-1 d-flex align-items-center justify-content-center"
                  style={{ fontSize: '0.75rem' }}
                  onClick={() => setSelectedLabel('')}
                  title="Clear filter"
                >
                  <BadgeMinus style={{ width: '16px', height: '16px' }} />
                </Button>
              )}
            </div>
            <div className="event-types-list">
              {Object.keys(eventTypeColors).slice(0, 3).map(type => {
                if (type === 'default') return null;
                return (
                  <div 
                    key={type}
                    className={`event-type-item ${selectedLabel === type ? 'active' : ''}`}
                    onClick={() => setSelectedLabel(selectedLabel === type ? '' : type)}
                    style={{
                      backgroundColor: selectedLabel === type ? `${eventTypeColors[type]}40` : 'transparent'
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <span className="smaller">{type}</span>
                      <div 
                        className="event-type-dot"
                        style={{ backgroundColor: eventTypeColors[type] }}
                      ></div>
                    </div>
                  </div>
                );
              })}
              {Object.keys(eventTypeColors).length > 4 && (
                <div className="show-more-container">
                  <button 
                    className="btn btn-sm btn-link text-muted p-0"
                    onClick={() => setShowAllEventTypes(!showAllEventTypes)}
                    style={{fontSize: '0.75rem'}}
                  >
                    {showAllEventTypes ? 'Show Less' : `Show ${Object.keys(eventTypeColors).length - 3} More`}
                  </button>
                </div>
              )}
              {showAllEventTypes && Object.keys(eventTypeColors).slice(3).map(type => {
                if (type === 'default') return null;
                return (
                  <div 
                    key={type}
                    className={`event-type-item ${selectedLabel === type ? 'active' : ''}`}
                    onClick={() => setSelectedLabel(selectedLabel === type ? '' : type)}
                    style={{ 
                      backgroundColor: selectedLabel === type ? `${eventTypeColors[type]}40` : 'transparent'
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <span className="smaller">{type}</span>
                      <div 
                        className="event-type-dot"
                        style={{ backgroundColor: eventTypeColors[type] }}
                      ></div>
                    </div>
                  </div>
                );
              })} 
            </div>
          </div>

          {/* Calendar Subscriptions */}
          <div className="calendar-subscriptions mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="text-muted mb-0 small">
                Calendar Subscriptions
              </h6>
              <button 
                className="btn btn-sm btn-link text-muted p-0"
                style={{ fontSize: '0.75rem' }}
                title="Add subscription"
                onClick={() => setShowSubscriptionModal(true)}
              >
                <CirclePlus style={{ width: '16px', height: '16px' }} />
              </button>
            </div>
            <div className="subscriptions-list d-flex a">
              {subscriptions.length === 0 ? (
                <div className="text-center py-3">
                  <CalendarPlus style={{ width: '32px', height: '32px'  }} className="mb-2" />
                  <p className="text-muted small mb-2">No calendar subscriptions</p>
                  <Button 
                    variant="light-solid"
                    size="sm"
                    onClick={() => setShowSubscriptionModal(true)}
                    style={{ fontSize: '0.75rem' }}
                    className="d-flex align-items-center gap-2"
                  >
                    <CirclePlus style={{ width: '16px', height: '16px' }} />
                    <span>Add Subscription</span>
                  </Button>
                </div>
              ) : (
                subscriptions.map(subscription => (
                  <div 
                    key={subscription.id}
                    className="subscription-item d-flex w-100"
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center  gap-2">
                        <Dropdown>
                          <Dropdown.Toggle
                            variant="link"
                            size="sm"
                            className="p-0 text-decoration-none"
                            style={{ fontSize: '0.7rem' }}
                          >
                            <div 
                              className="color-picker-indicator"
                              style={{ 
                                backgroundColor: subscription.color || subscriptionColors[0],
                                width: '14px',
                                height: '14px',
                                borderRadius: '2px',
                                border: '1px solid rgba(0,0,0,0.2)',
                                cursor: 'pointer'
                              }}
                              title="Change color"
                            ></div>
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <div className="p-2">
                              <div className="d-flex flex-wrap gap-1" style={{ maxWidth: '200px' }}>
                                {subscriptionColors.map((color, index) => (
                                  <div
                                    key={index}
                                    className="color-option"
                                    style={{
                                      backgroundColor: color,
                                      width: '24px',
                                      height: '24px',
                                      borderRadius: '4px',
                                      border: subscription.color === color ? '2px solid #333' : '1px solid rgba(0,0,0,0.2)',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onClick={() => {
                                      setSubscriptions(prev => prev.map(sub => 
                                        sub.id === subscription.id 
                                          ? { ...sub, color: color }
                                          : sub
                                      ));
                                    }}
                                    title={`Color ${index + 1}`}
                                  ></div>
                                ))}
                              </div>
                            </div>
                          </Dropdown.Menu>
                        </Dropdown>
                        <div 
                          className="d-flex align-items-center flex-grow-1"
                          onClick={() => {
                            setSubscriptions(prev => prev.map(sub => 
                              sub.id === subscription.id 
                                ? { ...sub, status: sub.status === 'active' ? 'inactive' : 'active' }
                                : sub
                            ));
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <span style={{ fontSize: '0.75rem' }}>{subscription.name}</span>
                          <div className={`subscription-status ${subscription.status} ms-2`}></div>
                        </div>
                      </div>
                      {subscription.type !== 'internal' && (
                        <button
                          className="btn btn-sm btn-link text-danger p-0"
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm(`Remove subscription to "${subscription.name}"?`)) {
                              try {
                                const token = localStorage.getItem("token");
                                if (!token) {
                                  console.error("No authentication token found");
                                  return;
                                }
                                await axios.delete(`${API_ENDPOINTS.CALENDAR_SUBSCRIPTIONS}/${subscription.id}`, {
                                  headers: {
                                    Authorization: `Bearer ${token}`
                                  }
                                });
                                setSubscriptions(prev => prev.filter(sub => sub.id !== subscription.id));
                                setNotificationMessage(`Subscription to "${subscription.name}" removed`);
                                setShowNotification(true);
                                setTimeout(() => setShowNotification(false), 3000);
                              } catch (error) {
                                console.error('Error removing subscription:', error);
                              }
                            }
                          }}
                          title="Remove subscription"
                          style={{ fontSize: '0.7rem' }}
                        >
                          <i className="bi bi-x-lg"></i>
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Share My Calendar */}
          <div className="share-calendar mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="text-muted mb-0 small">
                Share Calendar
              </h6>
            </div>
            <Button
              variant="light-solid"
              size="sm"
              className="w-100 d-flex align-items-center justify-content-center gap-2"
              onClick={() => setShowShareModal(true)}
              style={{ fontSize: '0.75rem' }}
            >
              <Share2 style={{ width: '16px', height: '16px' }} />
              <span>Share Calendar</span>
            </Button>
          </div>
        </Card>
      </Col>
    )
  );

  // Fetch subscriptions from database
  const fetchSubscriptions = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No authentication token found");
        return;
      }

      console.log('Fetching subscriptions from API...');
      const response = await axios.get(`${API_ENDPOINTS.CALENDAR_SUBSCRIPTIONS}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log('Fetched subscriptions:', response.data);
      setSubscriptions(response.data);
    } catch (error) {
      console.error("Error fetching calendar subscriptions:", error);
    }
  };

  // Load subscriptions on component mount
  useEffect(() => {
    fetchSubscriptions();
  }, []);

  // Save showMyCalendar setting to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('showMyCalendar', JSON.stringify(showMyCalendar));
    } catch (error) {
      console.error('Error saving showMyCalendar to localStorage:', error);
    }
  }, [showMyCalendar]);

  // Update share options when user ID changes
  useEffect(() => {
    const userId = getCurrentUserId();
    if (userId) {
      // Use API_BASE_URL for the iCal feed to point to the backend
      const apiBaseUrl = process.env.NODE_ENV === 'production' 
        ? (process.env.REACT_APP_API_URL || 'https://medhive-i0to.onrender.com')
        : 'http://localhost:5000';
        
      setShareOptions([
        { id: 1, name: 'Public Link', status: 'active', url: `${window.location.origin}/calendar/${userId}` },
        { id: 2, name: 'iCal Feed', status: 'active', url: `${apiBaseUrl}/api/events/feed/${userId}.ics` },
        { id: 3, name: 'Google Calendar', status: 'inactive', url: null }
      ]);
    }
  }, []);



  // Helper function to extract userId from shared calendar URLs
  const extractUserIdFromUrl = (url) => {
    try {
      console.log(`Extracting userId from URL: ${url}`);
      
      // Handle /calendar/:userId format
      if (url.includes('/calendar/')) {
        const match = url.match(/\/calendar\/([^\/\?]+)/);
        const userId = match ? match[1] : null;
        console.log(`Path parameter extraction: ${userId}`);
        return userId;
      }
      // Handle /calendar?user=:userId format
      if (url.includes('/calendar?user=')) {
        const urlObj = new URL(url);
        const userId = urlObj.searchParams.get('user');
        console.log(`Query parameter extraction: ${userId}`);
        return userId;
      }
      // Handle /api/events/feed/:userId.ics format
      if (url.includes('/api/events/feed/')) {
        const match = url.match(/\/api\/events\/feed\/([^\/]+)\.ics/);
        const userId = match ? match[1] : null;
        console.log(`iCal feed extraction: ${userId}`);
        return userId;
      }
      
      console.log('No matching URL pattern found');
      return null;
    } catch (error) {
      console.error('Error extracting userId from URL:', error);
      return null;
    }
  };





  // If ?date=YYYY-MM-DD is present, open in day view for that date
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dateParam = params.get('date');
    if (dateParam) {
      const parsed = new Date(dateParam);
      if (!isNaN(parsed)) {
        setDate(parsed);
        setView('day');
      }
    }
  }, [location.search]);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No authentication token found");
        return;
      }

      // Fetch user's own events
      const res = await axios.get(`${API_ENDPOINTS.EVENTS}/my-events`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      let allEvents = res.data.map(event => {
        // Convert UTC times to local timezone for display
        const startDate = moment.utc(event.start).local().toDate();
        const endDate = moment.utc(event.end).local().toDate();
        
        // Add debug logging for conference events
        if (event.label && (event.label.includes('Conference') || event.label.includes('M&M') || event.label.includes('Ground'))) {
          console.log('=== CALENDAR EVENT DEBUG ===');
          console.log('Event title:', event.title);
          console.log('Original start (from server):', event.start);
          console.log('Original end (from server):', event.end);
          console.log('Converted start (Date object):', startDate);
          console.log('Converted end (Date object):', endDate);
          console.log('Start time (formatted):', moment(startDate).format('h:mm A'));
          console.log('End time (formatted):', moment(endDate).format('h:mm A'));
          console.log('Start time (UTC):', moment.utc(event.start).format('h:mm A'));
          console.log('End time (UTC):', moment.utc(event.end).format('h:mm A'));
          console.log('Current timezone offset:', moment().format('Z'));
          console.log('==============================');
        }
        
        // For all-day events, ensure they run from 12:00 AM on start day to 11:59 PM on end day
        if (event.allDay) {
          const start = moment(startDate).startOf('day').toDate();
          const end = moment(endDate).startOf('day').add(23, 'hours').add(59, 'minutes').toDate();
          return {
            ...event,
            start: start,
            end: end,
            allDay: true,
            source: 'own', // Mark as user's own event
            subscriptionName: 'My Calendar' // Group all user events under "My Calendar"
          };
        }
        
        return {
          ...event,
          start: startDate,
          end: endDate,
          allDay: event.allDay || false,
          source: 'own', // Mark as user's own event
          subscriptionName: 'My Calendar' // Group all user events under "My Calendar"
        };
      });

      // Fetch events from subscribed calendars concurrently so a user with
      // several subscribed feeds isn't blocked waiting on each one in turn.
      const activeSubscriptions = subscriptions.filter(sub => sub.status === 'active');
      console.log('Active subscriptions:', activeSubscriptions);

      const apiBaseUrl = process.env.NODE_ENV === 'production'
        ? (process.env.REACT_APP_API_URL || 'https://medhive-i0to.onrender.com')
        : 'http://localhost:5000';

      const loadSubscriptionEvents = async (subscription) => {
        try {
          console.log(`Processing subscription: ${subscription.name} (type: ${subscription.type})`);
          let subscriptionEvents = [];

          if (subscription.type === 'url' && subscription.url) {
            const isInternalSharedCalendar = subscription.url.includes(`${window.location.origin}/calendar/`) ||
                                             subscription.url.includes(`${window.location.origin}/calendar?user=`);
            const isInternalICalFeed = subscription.url.includes(`${apiBaseUrl}/api/events/feed/`);

            if (isInternalSharedCalendar) {
              const userId = extractUserIdFromUrl(subscription.url);
              if (!userId) {
                console.error('Could not extract userId from URL:', subscription.url);
                return [];
              }
              try {
                const response = await axios.get(`${API_ENDPOINTS.EVENTS}/public/${userId}`);
                subscriptionEvents = response.data.map(event => {
                  const startDate = moment.utc(event.start).local().toDate();
                  const endDate = moment.utc(event.end).local().toDate();
                  if (event.allDay) {
                    const start = moment(startDate).startOf('day').toDate();
                    const end = moment(endDate).startOf('day').add(23, 'hours').add(59, 'minutes').toDate();
                    return { ...event, start, end, allDay: true };
                  }
                  return { ...event, start: startDate, end: endDate, allDay: event.allDay || false };
                });
              } catch (error) {
                console.error(`Error fetching internal shared calendar for user ${userId}:`, error);
              }
            } else if (isInternalICalFeed) {
              try {
                subscriptionEvents = await fetchEventsFromUrl(subscription.url, subscription.name);
              } catch (error) {
                console.error(`Error fetching internal iCal feed from URL ${subscription.url}:`, error);
              }
            } else if (subscription.name === 'Program Birthdays' && subscription.type === 'internal') {
              try {
                const token = localStorage.getItem("token");
                if (!token) return [];
                const programIdMatch = subscription.url.match(/program-birthdays-(\d+)/);
                if (!programIdMatch) return [];
                const programId = programIdMatch[1];
                const response = await axios.get(`${API_ENDPOINTS.EVENTS}/birthdays/${programId}`, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                subscriptionEvents = response.data.map(event => ({
                  ...event,
                  start: moment.utc(event.start).local().toDate(),
                  end: moment.utc(event.end).local().toDate(),
                  allDay: true,
                  label: 'Birthday',
                  source: 'subscription',
                  subscriptionName: 'Program Birthdays',
                }));
              } catch (error) {
                console.error('Error fetching birthday events for program:', error);
              }
            } else {
              subscriptionEvents = await fetchEventsFromUrl(subscription.url, subscription.name);
            }
          } else if (subscription.type === 'google') {
            subscriptionEvents = await fetchEventsFromGoogle(subscription.name);
          } else if (subscription.type === 'outlook') {
            subscriptionEvents = await fetchEventsFromOutlook(subscription.name);
          }

          return subscriptionEvents.map(event => ({
            ...event,
            source: 'subscription',
            subscriptionName: subscription.name,
            subscriptionId: subscription.id,
          }));
        } catch (error) {
          console.error(`Error fetching events from subscription "${subscription.name}":`, error);
          return [];
        }
      };

      const subscriptionResults = await Promise.all(activeSubscriptions.map(loadSubscriptionEvents));
      for (const events of subscriptionResults) {
        allEvents = allEvents.concat(events);
      }

      setEvents(allEvents);
    } catch (error) {
      console.error("Error fetching events", error);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      try {
        await Promise.all([fetchEvents(), fetchPendingInvitationsCount()]);
      } finally {
        setLoading(false);
      }
    };
    
    initializeData();
  }, [subscriptions]); // Re-fetch events when subscriptions change

  const fetchPendingInvitationsCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error("No authentication token found");
        return;
      }

      const response = await axios.get(`${API_ENDPOINTS.EVENT_INVITATIONS}/my-invitations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const pendingCount = response.data.filter(invitation => invitation.status === 'pending').length;
      setPendingInvitationsCount(pendingCount);
    } catch (error) {
      console.error('Error fetching pending invitations count:', error);
    }
  };

  const handleEventAdded = (newEvents) => {
    // If it's an array (recurring events), add all of them
    if (Array.isArray(newEvents)) {
              const formattedEvents = newEvents.map(event => {
          // Convert UTC times to local timezone for display
          const startDate = moment.utc(event.start).local().toDate();
          const endDate = moment.utc(event.end).local().toDate();
        
        // For all-day events, ensure they run from 12:00 AM on start day to 11:59 PM on end day
        if (event.allDay) {
          const start = moment(startDate).startOf('day').toDate();
          const end = moment(endDate).startOf('day').add(23, 'hours').add(59, 'minutes').toDate();
          return {
            ...event,
            start: start,
            end: end,
            allDay: true,
          };
        }
        
        return {
          ...event,
          start: startDate,
          end: endDate,
          allDay: event.allDay || false,
          subscriptionName: 'My Calendar'
        };
      });
      setEvents(prev => [...prev, ...formattedEvents]);
    } else {
      // Single event
      // Convert UTC times to local timezone for display
      const startDate = moment.utc(newEvents.start).local().toDate();
      const endDate = moment.utc(newEvents.end).local().toDate();
      
      // For all-day events, ensure they run from 12:00 AM on start day to 11:59 PM on end day
      if (newEvents.allDay) {
        const start = moment(startDate).startOf('day').toDate();
        const end = moment(endDate).startOf('day').add(23, 'hours').add(59, 'minutes').toDate();
        const formattedEvent = {
          ...newEvents,
          start: start,
          end: end,
          allDay: true,
        };
        setEvents(prev => [...prev, formattedEvent]);
      } else {
        const formattedEvent = {
          ...newEvents,
          start: startDate,
          end: endDate,
          allDay: newEvents.allDay || false,
          subscriptionName: 'My Calendar'
        };
        setEvents(prev => [...prev, formattedEvent]);
      }
    }
    // Refresh the page
    window.location.reload();
  };

  const handleEventUpdated = (updatedEvent) => {
    setEvents(prev => prev.map(event => {
      if (event.id === updatedEvent.id) {
        // Convert UTC times to local timezone for display
        const startDate = moment.utc(updatedEvent.start).local().toDate();
        const endDate = moment.utc(updatedEvent.end).local().toDate();
        
        // For all-day events, ensure they run from 12:00 AM on start day to 11:59 PM on end day
        if (updatedEvent.allDay) {
          const start = moment(startDate).startOf('day').toDate();
          const end = moment(endDate).startOf('day').add(23, 'hours').add(59, 'minutes').toDate();
          return {
            ...updatedEvent,
            start: start,
            end: end,
            allDay: true,
          };
        }
        
        return {
          ...updatedEvent,
          start: startDate,
          end: endDate,
          allDay: updatedEvent.allDay || false,
          subscriptionName: 'My Calendar'
        };
      }
      return event;
    }));
  };

  const handleEventDeleted = (eventId) => {
    setEvents(prev => prev.filter(event => event.id !== eventId));
    // Refresh the page
    window.location.reload();
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setIsDetailsModalOpen(true);
  };

  const handleEditClick = () => {
    // Don't allow editing subscription events
    if (selectedEvent && selectedEvent.source === 'subscription') {
      console.log('Cannot edit subscription events');
      return;
    }
    
    setIsDetailsModalOpen(false);
    setIsEditModalOpen(true);
  };

  const handleNavigate = (newDate) => {
    setDate(newDate);
    // Clear selected day when navigating to a different date
    setSelectedDay(null);
    // If in week view, set the selected day to the clicked date
    if (view === 'week') {
      setSelectedDay(newDate);
    }
  };

  const handleMiniCalendarDateChange = (newDate) => {
    setDate(newDate);
    // Set the selected day for highlighting in the main calendar
    setSelectedDay(newDate);
  };

  const handleView = (newView) => {
    setView(newView);
    // Clear selected day when changing views
    setSelectedDay(null);
  };



  const handleSelectSlot = (slotInfo) => {
    const selectedDate = new Date(slotInfo.start);
    const selectedEnd = new Date(slotInfo.end);
    
    // Check if the selection is in the all-day cell
    const isAllDay = slotInfo.action === 'select' && 
                    slotInfo.slots && 
                    slotInfo.slots.length > 0 && 
                    slotInfo.slots[0].getHours() === 0 && 
                    slotInfo.slots[0].getMinutes() === 0;
    
    // Format dates for the form fields
    const formattedStart = isAllDay 
      ? moment.utc(selectedDate).local().format('YYYY-MM-DD')
      : moment.utc(selectedDate).local().format('YYYY-MM-DDTHH:mm');
    const formattedEnd = isAllDay
      ? moment.utc(selectedEnd).local().startOf('day').add(23, 'hours').add(59, 'minutes').format('YYYY-MM-DDTHH:mm')
      : moment.utc(selectedEnd).local().format('YYYY-MM-DDTHH:mm');
    
    // Update the selected day for highlighting
    setSelectedDay(selectedDate);
    
    setSelectedSlot({
      ...slotInfo,
      start: selectedDate,
      end: selectedEnd,
      allDay: isAllDay,
      formattedStart,
      formattedEnd
    });
    setIsModalOpen(true);
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLabel = !selectedLabel || event.label === selectedLabel;
    const matchesMyCalendar = showMyCalendar || event.subscriptionName !== 'My Calendar';
    return matchesSearch && matchesLabel && matchesMyCalendar;
  });

  const exportToICS = () => {
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Calendar App//EN',
      ...events.map(event => {
        // For all-day events, use DATE format (YYYYMMDD) instead of DATETIME format
        const formatDate = (date) => {
          if (event.allDay) {
            return moment(date).format('YYYYMMDD');
          } else {
            return moment(date).format('YYYYMMDDTHHmmss');
          }
        };
        
        const formatDateType = (date) => {
          if (event.allDay) {
            return `DTSTART;VALUE=DATE:${formatDate(date)}`;
          } else {
            return `DTSTART:${formatDate(date)}`;
          }
        };
        
        const formatEndDateType = (date) => {
          if (event.allDay) {
            // For all-day events, end date should be the day after the last day
            const endDate = moment(date).add(1, 'day');
            return `DTEND;VALUE=DATE:${endDate.format('YYYYMMDD')}`;
          } else {
            return `DTEND:${formatDate(date)}`;
          }
        };
        
        return [
          'BEGIN:VEVENT',
          formatDateType(event.start),
          formatEndDateType(event.end),
          `SUMMARY:${event.title}`,
          event.description ? `DESCRIPTION:${event.description.replace(/\n/g, '\\n')}` : '',
          event.location ? `LOCATION:${event.location}` : '',
          event.label ? `CATEGORIES:${event.label}` : '',
          'END:VEVENT'
        ].filter(Boolean).join('\n');
      }),
      'END:VCALENDAR'
    ].join('\n');

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    saveAs(blob, 'calendar_export.ics');
  };

  const handlePrint = () => {
    const groupedEvents = getGroupedEvents(events, selectedMonth);
    const monthLabel = moment(selectedMonth).format('MMMM YYYY');
    
    const calendarContent = isListView ? 
      Object.entries(groupedEvents)
        .filter(([_, events]) => events.length > 0)
        .map(([dayKey, events]) => `
          <div class="mb-4">
            <h4>${moment(dayKey).format('dddd, MMMM D')}</h4>
            ${events.map(event => `
              <div class="event-item">
                <div class="event-title">
                  ${event.title}
                  ${event.label ? `<span class="event-label" style="background-color: ${eventTypeColors[event.label] || eventTypeColors.default} !important; color: #000 !important;">${event.label}</span>` : ''}
                </div>
                <div class="event-time">
                  ${event.allDay ? 'All Day' : `${moment.utc(event.start).local().format('h:mm A')} - ${moment.utc(event.end).local().format('h:mm A')}`}
                </div>
              </div>
            `).join('')}
          </div>
        `).join('')
      : `
        <div class="calendar-view">
          <div class="calendar-grid">
            ${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => 
              `<div class="calendar-day-header">${day}</div>`
            ).join('')}
            ${(() => {
              const monthStart = moment(selectedMonth).startOf('month');
              const monthEnd = moment(selectedMonth).endOf('month');
              const startDate = monthStart.clone().startOf('week');
              const endDate = monthEnd.clone().endOf('week');
              const days = [];
              
              let currentDate = startDate.clone();
              while (currentDate.isSameOrBefore(endDate)) {
                const dayKey = currentDate.format('YYYY-MM-DD');
                const isCurrentMonth = currentDate.month() === monthStart.month();
                const dayEvents = groupedEvents[dayKey] || [];
                
                days.push(`
                  <div class="calendar-day ${!isCurrentMonth ? 'text-muted' : ''}">
                    <div class="calendar-day-number">${currentDate.date()}</div>
                    ${dayEvents.map(event => `
                      <div class="calendar-event ${event.allDay ? 'all-day' : 'regular'}" 
                           style="background-color: ${eventTypeColors[event.label] || eventTypeColors.default}60 !important;">
                        <div class="text-truncate">${event.title}</div>
                        ${!event.allDay ? `<small>${moment.utc(event.start).local().format('h:mm A')}</small>` : ''}
                      </div>
                    `).join('')}
                  </div>
                `);
                
                currentDate.add(1, 'day');
              }
              return days.join('');
            })()}
          </div>
        </div>
        ${!isListView ? `
          <div class="sidebar">
            <h5 class="mb-2">Events</h5>
            ${Object.entries(groupedEvents)
              .filter(([_, events]) => events.length > 0)
              .map(([dayKey, events]) => `
                <div class="sidebar-day">
                  <div class="sidebar-day-header">${moment(dayKey).format('ddd, MMM D')}</div>
                  ${events.map(event => `
                    <div class="sidebar-event" style="border-left-color: ${eventTypeColors[event.label] || eventTypeColors.default} !important;">
                      <div class="sidebar-event-title">
                        <span class="text-truncate">${event.title}</span>
                        ${event.label ? `<span class="event-label" style="background-color: ${eventTypeColors[event.label] || eventTypeColors.default} !important; color: #000 !important;">${event.label}</span>` : ''}
                      </div>
                      <div class="sidebar-event-time">
                        ${event.allDay ? 'All Day' : `${moment.utc(event.start).local().format('h:mm A')} - ${moment.utc(event.end).local().format('h:mm A')}`}
                      </div>
                    </div>
                  `).join('')}
                </div>
              `).join('')}
          </div>
        ` : ''}
      `;

    const customStyles = `
      <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/css/bootstrap.min.css" rel="stylesheet">
      .event-item { margin-bottom: 10px; padding: 10px; border: 1px solid #dee2e6; border-radius: 4px; }
      .event-title { font-weight: bold; }
      .event-time { color: #6c757d; }
      .event-label { 
        display: inline-block; 
        padding: 2px 8px; 
        border-radius: 4px; 
        margin-left: 8px;
        color: #000 !important;
      }
      .calendar-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 1px; background: #dee2e6; }
      .calendar-day { background: white; min-height: 100px; padding: 8px; }
      .calendar-day-header { background: #f8f9fa; padding: 8px; text-align: center; font-weight: bold; }
      .calendar-day-number { font-size: 0.9rem; color: #6c757d; margin-bottom: 4px; }
      .calendar-event { font-size: 0.8rem; padding: 2px 4px; margin: 2px 0; border-radius: 3px; }
      .calendar-event.all-day { background: #e9ecef; }
      .calendar-event.regular { background: #f8f9fa; }
      .sidebar { border-left: 1px solid #dee2e6; padding-left: 15px; }
      .sidebar-event { 
        margin-bottom: 4px; 
        padding: 4px 8px; 
        border-left: 3px solid #dee2e6; 
        font-size: 0.85rem;
      }
      .sidebar-event-title {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
      }
      .sidebar-event-time {
        font-size: 0.75rem;
        color: #6c757d;
      }
      .sidebar-day {
        margin-bottom: 8px;
      }
      .sidebar-day-header {
        font-size: 0.8rem;
        color: #6c757d;
        margin-bottom: 2px;
        font-weight: 500;
      }
      .calendar-grid { page-break-inside: avoid; }
      .sidebar { page-break-inside: avoid; }
      .calendar-day,
      .calendar-day-header {
        border-color: #dee2e6 !important;
        border-style: solid !important;
        border-width: 1px !important;
        margin: -1 !important;
      }
      .sidebar-event {
        border-top: 1px solid #dee2e6 !important;
        border-bottom: 1px solid #dee2e6 !important;
        border-right: 1px solid #dee2e6 !important;
        border-radius: 4px !important;
        margin-bottom: 10px !important;
      }
      .calendar-day { background-color: white !important; }
      .calendar-day-header { background-color: #f8f9fa !important; }
      .calendar-event.all-day { background-color: #e9ecef !important; }
      .calendar-event.regular { background-color: #f8f9fa !important; }
      .text-muted { color: #6c757d !important; }
      .sidebar-event-time { color: #6c757d !important; }
      .calendar-day-number { color: #6c757d !important; }
      .event-label {
        background-color: inherit !important;
        color: #000 !important;
      }
    `;

    const printContent = buildPrintDocument({
      title: `Calendar - ${monthLabel}`,
      content: `<div class="print-section">${calendarContent}</div>`,
      headerTitle: `Calendar - ${monthLabel}`,
      showPrintButton: true,
      customStyles
    });

    openPrintWindow(printContent);
  };

  const renderListView = () => {
    const sortedEvents = [...filteredEvents].sort((a, b) => a.start - b.start);
    const groupedEvents = getGroupedEvents(sortedEvents, selectedMonth);
    
    // Filter out days with no events
    const daysWithEvents = Object.entries(groupedEvents).filter(([_, events]) => events.length > 0);

    return (
      <div className="border-start border-end bg-inverse-light" style={{ 
        height: 'calc(100vh - 115px)',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}>
        <MonthMenu 
          currentMonth={selectedMonth}
          onMonthChange={setSelectedMonth}
        />

        {daysWithEvents.length > 0 ? (
          daysWithEvents.map(([dayKey, events]) => (
            <div 
              key={dayKey} 
              className="day-group mb-4 gap-3 px-3"
              data-date={dayKey}
            >
              <div className="day-header mb-3">
                <h4 className="fw-semibold text-muted fs-6 px-3">
                  {moment(dayKey).format('dddd, MMMM D')}
                  {moment(dayKey).isSame(moment(), 'day') && (
                    <span className="badge bg-primary ms-2" style={{ fontSize: '0.8rem' }}>Today</span>
                  )}
                </h4>
              </div>
              
              <div className="d-flex flex-column gap-2">
                {events.map((event) => (
                  <div 
                    key={`${event.id}-${dayKey}`} 
                    className="bg-light rounded-3 overflow-hidden event-hover-effect" 
                    onClick={() => handleEventClick(event)}
                  >
                    <div className="d-flex flex-md-row flex-column justify-content-md-between justify-content-start align-items-md-center align-items-start p-3 gap-2">
                        <div className="d-flex flex-column  justify-content-start">
                          <h6 className="fw-semibold mb-0">
                            {event.title}
                          </h6>
                          <div className="d-flex align-items-center gap-2 text-muted small">
                            
                            {event.allDay ? <CalendarCheck/> : <Clock/>}
                            <span className="text-nowrap">
                              {event.allDay ? 'All Day' : `${moment.utc(event.start).local().format('h:mm A')} - ${moment.utc(event.end).local().format('h:mm A')}`}
                            </span>
                            {event.description && (
                              <>
                                <span className="mx-1">|</span>
                                <span className="text-truncate">{event.description}</span>
                              </>
                            )}

                          </div>
                          
                        </div>
                        <div className="d-flex flex-row-reverse align-items-center justify-content-md-end justify-content-between gap-2">
                          {event.label && (
                            <span className="pill-label rounded-pill py-1 px-3" style={{ 
                              backgroundColor: eventTypeColors[event.label] || eventTypeColors.default,
                              color: '#000',
                              fontSize: '0.75rem',
                              fontWeight: '500'
                            }}>
                              {event.label}
                            </span>
                          )}
                          {event.subscriptionName && (
                            <span className=" d-flex align-items-center text-muted small gap-2">
                              <CalendarFold/>
                              {event.subscriptionName}
                            </span>
                          )}
                          {event.location && (
                            <span className=" d-flex align-items-center text-muted small gap-2">
  <MapPinHouse/> {event.location}
                            </span>
                          )}
                        </div> 
                        
                      
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '48px 0',
            color: '#6B7280'
          }}>
            <CalendarX size={48} className="mb-2" />
            <p>No events for this month</p>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={embedded ? "h-100" : ""}>
        {!hidePageChrome && <PageTitle title="Loading..." />}
        <CustomStyles />
        {!hidePageChrome && <Navbar pageTitle={pageTitle} />}

        <div className="bg-white border-bottom">
          <div className="container" style={{paddingTop: '10px', paddingBottom: '10px'}}>
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0 py-2 fw-semibold">Calendar</h6>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <Spinner 
              animation="border" 
              variant="primary" 
              size="lg"
              className="mb-3"
            />
            <div className="text-muted">Loading calendar events...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? "h-100" : ""}>
      {!hidePageChrome && <PageTitle title="Calendar" description="Manage your calendar events and schedule" />}
      <CustomStyles />
      {!hidePageChrome && <Navbar pageTitle={pageTitle} />}

      <div className="border-bottom  bg-white">
          <div className="container-fluid ">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3">
                
                <Button
                  variant="light-solid"
                  size="sm"
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="d-none d-lg-block"
                >
                  {sidebarCollapsed ? <PanelRightClose /> : <PanelRightOpen />}
                </Button>
              <h6 className="mb-0 fw-semibold py-3">Calendar</h6>
              </div>
                {showSearchPanel ? (
                  <div className="d-flex gap-2 align-items-center flex-grow-1 ms-3 justify-content-end">
                    <div className="d-flex gap-2 align-items-center w-100">
                      <InputGroup className="position-relative">
                        <Form.Control
                          placeholder="Search events..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          autoFocus
                        >
                          
                        </Form.Control>

                        <DropdownButton
                          variant="light"
                          className="w-auto"
                          title={
                            selectedLabel ? (
                              <>
                                <i className="bi bi-circle-fill me-2" style={{ 
                                  color: eventTypeColors[selectedLabel],
                                  fontSize: '0.75rem'
                                }}></i>
                                {' '}
                                {selectedLabel}
                              </>
                            ) : 'All Events'
                          }
                        >
                          <Dropdown.Item onClick={() => setSelectedLabel('')}>
                            <i className="bi bi-circle-fill me-2" style={{ 
                              color: '#6c757d',
                              fontSize: '0.75rem'
                            }}></i>
                            {' '}
                            All Events
                          </Dropdown.Item>
                          {Object.keys(eventTypeColors).map(type => {
                            if (type !== 'default') {
                              return (
                                <Dropdown.Item 
                                  key={type} 
                                  onClick={() => setSelectedLabel(type)}
                                >
                                  <i className="bi bi-circle-fill me-2" style={{ 
                                    color: eventTypeColors[type],
                                    fontSize: '0.75rem'
                                  }}></i>
                                  {' '}
                                  {type}
                                </Dropdown.Item>
                              );
                            }
                            return null;
                          })}
                        </DropdownButton>
                      <div className="position-absolute" style={{ right: '125px', top: '5px' }}>
                      <Button 
                        variant="light-solid" 
                        size="sm"
                        onClick={() => {
                          setSearchTerm('');
                          setSelectedLabel('');
                        }}
                        className={`px-2 py-1 ${searchTerm ? '' : 'd-none'}`}
                      >
                        Clear
                      </Button></div>
                      </InputGroup>

                      
                        <Button 
                        variant="light-solid"
                        size="sm"
                        onClick={() => setShowSearchPanel(false)}
                        className="d-flex flex-row align-items-center justify-content-center"
                      >
                        <div className="d-flex align-items-center gap-2"><X /></div>
                      </Button>
                      
                    </div>
                  </div>
                ) : (
                  <div className="d-flex gap-2">
                    
                    <div className="radio-inputs d-flex align-items-center">
                      <div className="dropdown-container">
                        <Dropdown className="d-none d-sm-block">
                          <Dropdown.Toggle variant="light-solid" size="sm"className="no-arrow">
                            <div className="d-flex align-items-center">
                            <Forward/>
                            <span className="d-none d-md-inline ms-2">Export</span></div>
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={exportToICS}>
                              <div className="d-flex align-items-center gap-2"><Calendar1/>
                              <span className="d-none d-md-inline ms-2">Calendar</span></div>
                            </Dropdown.Item>
                            <Dropdown.Item onClick={handlePrint}>
                              <div className="d-flex align-items-center gap-2"><Printer/>
                              <span className="d-none d-md-inline ms-2">Print</span></div>
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                      <label className="radio">
                        <input
                          type="radio"
                          name="calendar-actions"
                          onChange={() => setShowInvitationsModal(true)}
                          checked={showInvitationsModal}
                          style={{ position: 'absolute', left: 0, top: 0, opacity: 0, width: 0, height: 0 }}
                        />
                        <span className="name bg-white-hover px-2 d-flex align-items-center" style={{ position: 'relative' }}>
                          <Mailbox />
                          <span className="d-none d-md-block ms-2">Invites</span>
                          {pendingInvitationsCount > 0 && (
                            <Badge 
                              bg="danger" 
                              style={{ 
                                position: 'absolute', 
                                top: '-8px', 
                                right: '-18px', 
                                fontSize: '0.7rem',
                                minWidth: '18px',
                                height: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 1000,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                              }}
                            >
                              {pendingInvitationsCount}
                            </Badge>
                          )}
                        </span>
                      </label>
                      <label className="radio d-block d-md-none">
                        <input
                          type="checkbox"
                          name="calendar-menu-toggle"
                          onChange={() => setShowCalendarMenu(!showCalendarMenu)}
                          checked={showCalendarMenu}
                          style={{ position: 'absolute', left: 0, top: 0, opacity: 0, width: 0, height: 0 }}
                        />
                        <span className="name bg-white-hover px-2 d-flex align-items-center">
                          <i className={showCalendarMenu ? "bi bi-x-lg" : "bi bi-calendar3"}></i>
                          <span className="d-none d-md-block ms-2">{showCalendarMenu ? 'Close' : 'Calendars'}</span>
                        </span>
                      </label>
                      <label className="radio">
                        <input
                          type="radio"
                          name="calendar-actions"
                          onChange={() => setShowSearchPanel(true)}
                          checked={showSearchPanel }
                          style={{ position: 'absolute', left: 0, top: 0, opacity: 0, width: 0, height: 0 }}
                        />
                        <span className="name bg-white-hover px-2 d-flex align-items-center">
                        <CalendarSearch />
                        </span>
                      </label>
                    </div>
                    <div className="radio-inputs d-flex align-items-center">
                      <label className="radio">
                        <input
                          type="radio"
                          name="radio"
                          onChange={() => setIsListView(false)}
                          checked={!isListView}
                        />
                        <span className="name px-2"><CalendarDays /></span>
                      </label>
                      <label className="radio">
                        <input
                          type="radio"
                          name="radio"
                          onChange={() => setIsListView(true)}
                          checked={isListView}
                        />
                        <span className="name px-2"><Rows3 /></span>
                      </label>
                    </div>
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={() => setIsModalOpen(true)}
                      className="d-flex flex-row align-items-center justify-content-center text-nowrap"
                    >
                      <BadgePlus />
                      <span className="d-none d-md-inline ms-2">New Event</span>
                    </Button>
                  </div>
                )}
            </div>
            </div>
          </div>

        {/* Calendar Menu Panel */}
        {showCalendarMenu && (
          <div className="bg-white shadow-sm border-top">
            <div className="container py-3">
              <h6 className="mb-3 text-muted">
                <div className="d-flex align-items-center gap-2"><Calendar1/>
                <span className="d-none d-md-inline ms-2">Calendar Visibility</span></div>
                Calendar Visibility
              </h6>
              
              <div className="row">
                <div className="col-md-6">
                  <div className="d-flex align-items-center justify-content-between p-3 border rounded">
                    <div className="d-flex align-items-center">
                      <div 
                        className="calendar-color-indicator me-3"
                        style={{ 
                          backgroundColor: '#007bff',
                          width: '16px',
                          height: '16px',
                          borderRadius: '3px',
                          border: '1px solid rgba(0,0,0,0.2)'
                        }}
                      ></div>
                      <div>
                        <h6 className="mb-1">My Calendar</h6>
                        <small className="text-muted">Events you've created</small>
                      </div>
                    </div>
                    <Form.Check
                      type="switch"
                      checked={showMyCalendar}
                      onChange={(e) => setShowMyCalendar(e.target.checked)}
                      id="show-my-calendar"
                    />
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="d-flex align-items-center justify-content-between p-3 border rounded">
                    <div className="d-flex align-items-center">
                      <div 
                        className="calendar-color-indicator me-3"
                        style={{ 
                          backgroundColor: '#6c757d',
                          width: '16px',
                          height: '16px',
                          borderRadius: '3px',
                          border: '1px solid rgba(0,0,0,0.2)'
                        }}
                      ></div>
                      <div>
                        <h6 className="mb-1">Subscribed Calendars</h6>
                        <small className="text-muted">External calendar feeds</small>
                      </div>
                    </div>
                    <Form.Check
                      type="switch"
                      checked={true}
                      disabled
                      id="show-subscribed-calendars"
                    />
                  </div>
                </div>
              </div>
              
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="text-muted mb-0">Active Subscriptions</h6>
                  <button 
                    className="btn btn-sm btn-link text-muted p-0"
                    onClick={() => setShowSubscriptionModal(true)}
                    title="Add new subscription"
                  >
                    <i className="bi bi-plus-circle"></i>
                  </button>
                </div>
                {subscriptions.filter(sub => sub.status === 'active').length === 0 ? (
                  <div className="text-center py-3 border rounded">
                    <div className="d-flex align-items-center gap-2"><CalendarPlus/>
                    <span className="d-none d-md-inline ms-2">Add Subscription</span></div>
                    <p className="text-muted small mb-2">No active subscriptions</p>
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setShowSubscriptionModal(true)}
                    >
                      <i className="bi bi-plus-circle me-1"></i>
                      Add Subscription
                    </button>
                  </div>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {subscriptions.filter(sub => sub.status === 'active').map(subscription => (
                      <div 
                        key={subscription.id}
                        className="d-flex align-items-center p-2 border rounded"
                        style={{ 
                          backgroundColor: subscription.color + '20',
                          borderColor: subscription.color + '40'
                        }}
                      >
                        <div 
                          className="calendar-color-indicator me-2"
                          style={{ 
                            backgroundColor: subscription.color,
                            width: '12px',
                            height: '12px',
                            borderRadius: '2px'
                          }}
                        ></div>
                        <span className="small">{subscription.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      <Container fluid className="px-md-2 px-0 overflow-hidden bg-inverse-light">
              {/*<Row className="mb-3 mb-md-0">
                <Col lg={4} className="d-flex flex-row gap-3 d-none d-md-flex">

                  <div className="card shadow-sm border-0 flex-grow-1">
                    <div className="card-body p-2">
                      <h6 className="card-subtitle mb-1 text-muted" style={{ fontSize: '0.75rem' }}>Total Events</h6>
                      <h3 className="card-title mb-0" style={{ fontSize: '1.25rem' }}>{events.length}</h3>
                    </div>
                  </div>
                  <div className="card shadow-sm border-0 flex-grow-1">
                    <div className="card-body p-2">
                      <h6 className="card-subtitle mb-1 text-muted" style={{ fontSize: '0.75rem' }}>This Week</h6>
                      <h3 className="card-title mb-0" style={{ fontSize: '1.25rem' }}>
                        {events.filter(event => 
                          moment(event.start).isSame(moment(), 'week')
                        ).length}
                      </h3>
                    </div>
                  </div>
                  <div className="card shadow-sm border-0 flex-grow-1">
                    <div className="card-body p-2">
                      <h6 className="card-subtitle mb-1 text-muted" style={{ fontSize: '0.75rem' }}>Upcoming</h6>
                      <h3 className="card-title mb-0" style={{ fontSize: '1.25rem' }}>
                        {events.filter(event => 
                          moment(event.start).isAfter(moment())
                        ).length}
                      </h3>
                    </div>
                  </div>
                  </Col>
                </Row>*/}
            <Row className="g-0" style={{ display: 'flex', flexWrap: 'nowrap' }}>
              {isListView ? (
                <>
                  {/* Desktop Sidebar for List View */}
                  {renderSidebar()}
                    
                    {/* List View Content */}
                    <Col className="d-block" style={{ 
                      flex: '1 1 auto', 
                      minWidth: 0, 
                      height: 'calc(100vh - 114px)',
                      overflow: 'hidden'
                    }}>
                      {renderListView()}
                    </Col>
                  </>
                ) : (
                <>
                  {/* Desktop Sidebar */}
                  {renderSidebar()}
                  
                  {/* Mobile Mini Calendar */}
                  <Col className="d-none mb-3">
                    <div className="mobile-mini-calendar">
                      <MiniCalendar
                        currentDate={date}
                        events={events}
                        view={view}
                        onDateChange={handleMiniCalendarDateChange}
                        onNavigate={handleNavigate}
                        eventTypeColors={eventTypeColors}
                        selectedDay={selectedDay}
                      />
                    </div>
                  </Col>
                  
                  {/* Main Calendar */}
                  <Col className="d-lg-block">
                <div className="custom-calendar">
                  <BigCalendar
                    localizer={localizer}
                    events={filteredEvents}
                    startAccessor="start"
                    endAccessor="end"
                    style={{ height: '100%' }}
                    views={["month", "week", "day"]}
                    view={view}
                    onView={handleView}
                    date={date}
                    onNavigate={handleNavigate}
                    onSelectEvent={handleEventClick}
                    onSelectSlot={handleSelectSlot}
                    selectable={true}
                    defaultView="month"
                    popup={true}
                    toolbar={true}
                    step={15}
                    timeslots={1}
                    min={moment().startOf('day').add(.01, 'hours').toDate()}
                    max={moment().startOf('day').add(23.99, 'hours').toDate()}

                                                                eventPropGetter={(event) => {
                      let backgroundColor = eventTypeColors[event.label] || eventTypeColors.default;
                      
                      // Use subscription color if it's a subscription event
                      if (event.source === 'subscription' && event.subscriptionName) {
                        const subscription = subscriptions.find(sub => sub.name === event.subscriptionName);
                        if (subscription && subscription.color) {
                          backgroundColor = subscription.color;
                        }
                      }
                      
                      // For "My Calendar" events, use a distinct style
                      const isMyCalendar = event.subscriptionName === 'My Calendar';
                      
                      return {
                      style: {
                          backgroundColor: backgroundColor,
                        borderRadius: '4px',
                          opacity: isMyCalendar ? 1.0 : 0.8,
                        overflow: 'hidden'
                      },
                        className: `hide-default-time ${event.source === 'subscription' ? 'subscription-event' : ''} ${isMyCalendar ? 'my-calendar-event' : ''}`
                      };
                    }}
                    dayPropGetter={(date) => {
                      // Highlight the selected day from mini calendar
                      if (selectedDay && moment(date).isSame(moment(selectedDay), 'day')) {
                        if (view === 'week' || view === 'day') {
                          // In week view, don't highlight the background
                          return {};
                        } else {
                          // In month and day views, highlight the full day
                          return {
                            style: {
                              backgroundColor:'var(--indigo-light)',
                            }
                          };
                        }
                      }
                      return {};
                    }}
                    components={{
                      event: (props) => <CustomEvent {...props} view={view} />,
                      toolbar: CustomToolbar,
                      header: (props) => <CustomHeader {...props} selectedDay={selectedDay} />
                    }}
                    dayLayoutAlgorithm="no-overlap"
                    slotGroupPropGetter={() => ({
                      style: {
                        minHeight: '30px'
                      }
                    })}
                  />
                </div>
                  </Col>
                </>
              )}


              <EventDetailsModal
                isOpen={isDetailsModalOpen}
                onClose={() => setIsDetailsModalOpen(false)}
                event={selectedEvent}
                onEventUpdated={handleEventUpdated}
                onEventDeleted={handleEventDeleted}
                onEditClick={handleEditClick}
              />
            </Row>

            <AddEventModal
              show={isModalOpen}
              handleClose={() => setIsModalOpen(false)}
              onEventAdded={handleEventAdded}
              selectedSlot={selectedSlot}
            />

            <EditEventModal
              show={isEditModalOpen}
              handleClose={() => setIsEditModalOpen(false)}
              event={selectedEvent}
              onEventUpdated={handleEventUpdated}
            />
      </Container>

      <EventInvitationsModal
        show={showInvitationsModal}
        onHide={() => {
          setShowInvitationsModal(false);
          fetchPendingInvitationsCount(); // Refresh count when modal is closed
        }}
      />

        <SubscriptionModal
          show={showSubscriptionModal}
          onHide={() => setShowSubscriptionModal(false)}
          onSubscriptionAdded={(newSubscription) => {
            setSubscriptions(prev => [...prev, { ...newSubscription, id: Date.now() }]);
            setShowSubscriptionModal(false);
            setNotificationMessage(`Subscription to "${newSubscription.name}" added successfully`);
            setShowNotification(true);
            setTimeout(() => setShowNotification(false), 3000);
          }}
        />

        {/* Share Calendar Modal */}
        <Modal show={showShareModal} onHide={() => setShowShareModal(false)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <div className="d-flex align-items-center gap-2"><Share2/>
              <span className="d-none d-md-inline ms-2">Share My Calendar</span></div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row">
              <div className="col-12">
              <h6 className="mb-3">Choose how you'd like to share your calendar:</h6>
              </div>
              <div className="col-md-6">
                {/* Public Link */}
                <div className="mb-4">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="mb-0">
                      <div className="d-flex align-items-center gap-2"><Share2/>
                      <span className="d-none d-md-inline ms-2"> Public Link</span></div>
                    </h6>
                    <Badge bg="success">Active</Badge>
                  </div>
                  <p className="text-muted small mb-2">Share a public link that anyone can view</p>
                  <div className="input-group">
                    <input 
                      type="text" 
                      className="form-control" 
                      value={`${window.location.origin}/calendar?user=${getCurrentUserId()}`}
                      readOnly 
                    />
                    <Button 
                      variant="light"
                      onClick={() => {
                        const url = `${window.location.origin}/calendar?user=${getCurrentUserId()}`;
                        navigator.clipboard.writeText(url);
                        setShareModalContent({
                          title: 'URL Copied!',
                          message: 'Public calendar URL has been copied to your clipboard.',
                          url: url
                        });
                      }}
                    >
                      <Copy/>
                    </Button>
                  </div>
                </div>
                </div>
                <div className="col-md-6">

                {/* iCal Feed */}
                <div className="mb-4">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6 className="mb-0">
                      <div className="d-flex align-items-center gap-2"><CalendarCheck/>
                      <span className="d-none d-md-inline ms-2">iCal Feed</span></div>
                    </h6>
                    <Badge bg="success">Active</Badge>
                  </div>
                  <p className="text-muted small mb-2">Subscribe to this calendar in other applications</p>
                  <div className="input-group">
                    <input 
                      type="text" 
                      className="form-control" 
                      value={`${process.env.NODE_ENV === 'production' ? (process.env.REACT_APP_API_URL || 'https://medhive-i0to.onrender.com') : 'http://localhost:5000'}/api/events/feed/${getCurrentUserId()}.ics`}
                      readOnly 
                    />
                    <Button 
                      variant="light"
                      onClick={() => {
                        const apiBaseUrl = process.env.NODE_ENV === 'production' 
                          ? (process.env.REACT_APP_API_URL || 'https://medhive-i0to.onrender.com')
                          : 'http://localhost:5000';
                        const url = `${apiBaseUrl}/api/events/feed/${getCurrentUserId()}.ics`;
                        navigator.clipboard.writeText(url);
                        setShareModalContent({
                          title: 'URL Copied!',
                          message: 'iCal feed URL has been copied to your clipboard.',
                          url: url
                        });
                      }}
                    >
                      <Copy/>
                    </Button>
                  </div>
                </div>
                </div>
                <div className="col-12">

                {/* Calendar Instructions - Tabbed */}
                <div className="mb-4">
                  
                  <Tabs defaultActiveKey="google" id="calendar-instructions-tabs">
                    <Tab disabled title={<span className="text-muted fw-bold">Instructions</span>}>
                    </Tab>
                    <Tab eventKey="google" title={<span><i className="bi bi-google me-2"></i>Google</span>}>
                      <div className="border-top pt-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="mb-0">Google Calendar</h6>
                        <Badge bg="secondary">Manual Setup</Badge>
                      </div>
                      <p className="text-muted small mb-2">Add this calendar to Google Calendar</p>
                      <div className="alert alert-info">
                        <div className="d-flex align-items-start">
                          <Info/>
                          <div className="ms-2">
                            <strong>Instructions:</strong>
                            <ol className="mb-0 mt-2">
                              <li>Copy the iCal Feed URL above</li>
                              <li>Go to <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer">Google Calendar</a></li>
                              <li>In the left sidebar, click the + next to "Other calendars"</li>
                              <li>Select "From URL"</li>
                              <li>Paste the iCal URL and click "Add calendar"</li>
                              <li>The calendar will appear in your "Other calendars" section</li>
                            </ol>
                            <div className="mt-2">
                              <small className="text-muted">
                                <strong>Note:</strong> It may take a few minutes for events to appear. The calendar will automatically sync and update.
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                      </div>
                    </Tab>
                    <Tab eventKey="outlook" title={<span><i className="bi bi-microsoft me-2"></i>Outlook</span>}>
                      <div className="border-top pt-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="mb-0">Outlook Calendar</h6>
                        <Badge bg="secondary">Manual Setup</Badge>
                      </div>
                      <p className="text-muted small mb-2">Add this calendar to Outlook</p>
                      <div className="alert alert-info">
                        <div className="d-flex align-items-start">
                          <Info/>
                          <div className="ms-2">
                            <strong>Instructions:</strong>
                            <ol className="mb-0 mt-2">
                              <li>Copy the iCal Feed URL above</li>
                              <li>Open Outlook (desktop app or web)</li>
                              <li>Go to Calendar view</li>
                              <li>Right-click on "Other Calendars" in the left sidebar</li>
                              <li>Select "Add Calendar" → "From Internet"</li>
                              <li>Paste the iCal URL and click "OK"</li>
                              <li>The calendar will appear in your "Other Calendars" section</li>
                            </ol>
                            <div className="mt-2">
                              <small className="text-muted">
                                <strong>Note:</strong> For Outlook Web, look for "Add calendar" → "Subscribe from web" in the calendar settings.
                              </small>
                            </div>
                          </div>
                          </div>
                        </div>
                      </div>
                    </Tab>
                    <Tab eventKey="apple" title={<span><i className="bi bi-apple me-2"></i>Apple</span>}>
                      <div className="border-top pt-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="mb-0">Apple Calendar</h6>
                        <Badge bg="secondary">Manual Setup</Badge>
                      </div>
                      <p className="text-muted small mb-2">Add this calendar to Apple Calendar</p>
                      <div className="alert alert-info">
                        <div className="d-flex align-items-start">
                          <Info/>
                          <div className="ms-2">
                            <strong>Instructions:</strong>
                            <ol className="mb-0 mt-2">
                              <li>Copy the iCal Feed URL above</li>
                              <li>Open Calendar app on your Mac</li>
                              <li>Go to File → New Calendar Subscription</li>
                              <li>Paste the iCal URL and click "Subscribe"</li>
                              <li>Choose your preferred refresh interval (e.g., every 15 minutes)</li>
                              <li>The calendar will appear in your sidebar</li>
                            </ol>
                            <div className="mt-2">
                              <small className="text-muted">
                                <strong>Note:</strong> For iOS Calendar, you can also add the URL in Settings → Calendar → Accounts → Add Account → Other → Add Subscribed Calendar.
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                      </div>
                    </Tab>
                    <Tab eventKey="troubleshooting" title={<span><LifeBuoy /> Troubleshooting</span>}>
                      <div className="border-top pt-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="mb-0">Troubleshooting</h6>
                        <Badge bg="warning">Help</Badge>
                      </div>
                      <div className="alert alert-warning">
                        <div className="d-flex align-items-start">
                          <TriangleAlert/>
                          <div className="ms-2">
                            <strong>Common Issues:</strong>
                            <ul className="mb-0 mt-2">
                              <li><strong>No events appear:</strong> Wait 5-10 minutes for the first sync, or try refreshing the calendar</li>
                              <li><strong>URL not working:</strong> Make sure you copied the entire URL including "https://"</li>
                              <li><strong>Authentication error:</strong> The calendar feed is public and doesn't require login</li>
                              <li><strong>Events in wrong timezone:</strong> Events are set to Central Time (America/Chicago)</li>
                              <li><strong>Calendar not updating:</strong> External calendars typically sync every 15-60 minutes</li>
                            </ul>
                            <div className="mt-2">
                              <small className="text-muted">
                                <strong>Need help?</strong> Contact your program administrator if you continue to have issues.
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                      </div>
                    </Tab>
                  </Tabs>
                </div>

               
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowShareModal(false)}>
              Close
            </Button>
                    </Modal.Footer>
        </Modal>

        {/* Notification Toast */}
        {showNotification && (
          <div 
            style={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              zIndex: 9999,
              backgroundColor: '#28a745',
              color: 'white',
              padding: '12px 20px',
              borderRadius: '6px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              animation: 'slideInRight 0.3s ease-out'
            }}
          >
            <div className="d-flex align-items-center">
              <i className="bi bi-check-circle me-2"></i>
              {notificationMessage}
            </div>
          </div>
        )}

        <style>
          {`
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
          `}
        </style>
    </div>
  );
}